import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eventService } from '../api/eventService';
import { 
    Plus, 
    Trash2, 
    ChevronLeft, 
    ChevronRight, 
    FileSpreadsheet, 
    Info, 
    Download,
    Eye,
    CalendarDays,
    MessageSquare,
    Send,
    Lock,
    X,
    AlertTriangle,
    Upload,
    Check,
    Edit,
    Archive,
    Save
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { getDaysDifference } from '../utils/dateUtils';
import CustomTimePicker from '../components/CustomTimePicker';
import CustomDatePicker from '../components/CustomDatePicker';

const formatDateForDisplay = (val) => {
    if (!val) return '';
    const parts = val.split('-');
    if (parts.length === 3) {
        return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
    }
    return val;
};

// Time utilities for clock picker and overlap conflict checking
const parseTimeRange = (timeStr) => {
    if (!timeStr) return { start: '', end: '' };
    
    const parts = timeStr.split(/[-–—]|to/i);
    
    const cleanPart = (part) => {
        if (!part) return '';
        let digits = part.replace(/\D/g, '');
        
        if (part.includes(':')) {
            const timeParts = part.split(':');
            let h = timeParts[0].replace(/\D/g, '');
            let m = timeParts[1].replace(/\D/g, '');
            if (h && m) {
                return `${h.padStart(2, '0')}:${m.padEnd(2, '0').slice(0, 2)}`;
            }
        }
        
        if (digits.length === 3) {
            digits = '0' + digits;
        }
        if (digits.length === 4) {
            return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
        }
        if (digits.length === 1 || digits.length === 2) {
            return `${digits.padStart(2, '0')}:00`;
        }
        return '';
    };

    if (parts.length === 1) {
        return { start: cleanPart(parts[0]), end: '' };
    }
    
    return {
        start: cleanPart(parts[0]),
        end: cleanPart(parts[1])
    };
};

const formatTimeRange = (start, end) => {
    const cleanTime = (t) => {
        if (!t) return '';
        return t.replace(':', '');
    };
    const s = cleanTime(start);
    const e = cleanTime(end);
    if (!s && !e) return '';
    return `${s || '0000'} - ${e || '0000'}`;
};

const isValidTimeRange = (timeStr) => {
    if (!timeStr) return true;
    const { start, end } = parseTimeRange(timeStr);
    if (!start || !end) return true;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    return endMins > startMins;
};

const timeRangesOverlap = (timeStrA, timeStrB) => {
    if (!timeStrA || !timeStrB) return false;
    const { start: sA, end: eA } = parseTimeRange(timeStrA);
    const { start: sB, end: eB } = parseTimeRange(timeStrB);
    if (!sA || !eA || !sB || !eB) return false;
    
    const [shA, smA] = sA.split(':').map(Number);
    const [ehA, emA] = eA.split(':').map(Number);
    const [shB, smB] = sB.split(':').map(Number);
    const [ehB, emB] = eB.split(':').map(Number);
    
    const startA = shA * 60 + smA;
    const endA = ehA * 60 + emA;
    const startB = shB * 60 + smB;
    const endB = ehB * 60 + emB;
    
    if (endA <= startA || endB <= startB) return false;
    
    return startA < endB && startB < endA;
};

const formatTimeForDisplay = (timeStr) => {
    if (!timeStr) return '';
    const { start, end } = parseTimeRange(timeStr);
    if (!start && !end) return timeStr;
    return `${start || '??:??'} - ${end || '??:??'}`;
};

const MONTH_OPTIONS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const VENUE_OPTIONS = [
    'Type manually',
    'GP Hall',
    'Panchmukhi Room',
    'Marigold',
    'Maple',
    'Silver oak',
    'Gulmohar',
    'Chinar',
    'WILLOW',
    'Magnolia',
    'Casuarina',
    'Juniper',
    'Tamarind',
    'Cypress',
    'The Stein auditorium',
    'Mahagony',
    'Margosa',
    'Silk Cotton Lawn'
];

const MODE_OPTIONS = [
    'ONLINE',
    'OFFLINE',
    'HYBRID'
];

// Extracted from Verticals For RAC_260613_190757.pdf (Column 2: RIS Pillars - Functional Units)
const VERTICAL_OPTIONS = [
    'Development Cooperation',
    'SDG',
    'Trade and Investment',
    'Maritime and Connectivity',
    'Finance',
    'HEALTH',
    'Science and Technology',
    'Agriculture',
    'Other'
];

// Extracted from Verticals For RAC_260613_190757.pdf (Column 3: Division/Area of Work)
const SUB_VERTICAL_OPTIONS = {
    'Development Cooperation': [
        'Development Cooperation (SS +Triangular)',
        'DC Implementation',
        'Other'
    ],
    'SDG': [
        'Sustainable Development Goals',
        'Other'
    ],
    'Trade and Investment': [
        'WTO | IPR | BITS | Govt Procurement',
        'Standards',
        'Trade Facilitation',
        'Sustainability',
        'Trade and Technology',
        'Industry & Digital trade',
        'FTAs & Critical Minerals',
        'Other'
    ],
    'Maritime and Connectivity': [
        'CMEC',
        'Connectivity',
        'Blue Economy',
        'Maritime Connectivity',
        'Other'
    ],
    'Finance': [
        'Finance',
        'Other'
    ],
    'HEALTH': [
        'Forum on Indian Traditional Medicine',
        'Health (DAKSHIN)',
        'Other'
    ],
    'Science and Technology': [
        'Science and Diplomacy',
        'AI | Digital (DAKSHIN)',
        'Other'
    ],
    'Agriculture': [
        'Agriculture',
        'Trade and Agriculture',
        'Other'
    ],
    'Other': [
        'Other'
    ]
};

// Extracted from Verticals For RAC_260613_190757.pdf (Column 5: RIS Pillars - Strategic Unit)
const CENTER_OPTIONS = [
    'RIS + DAKSHIN',
    'RIS',
    'RIS + CMEC',
    'FITM',
    'Other'
];

// Extracted mappings from Verticals For RAC_260613_190757.pdf
// Maps a Sub-Vertical to its corresponding default Center and Coordinator
const SUB_VERTICAL_DEFAULTS = {
    'Development Cooperation (SS +Triangular)': {
        centerName: 'RIS + DAKSHIN',
        coordinator: 'DR. SUSHIL KUMAR'
    },
    'DC Implementation': {
        centerName: 'RIS + DAKSHIN',
        coordinator: 'DR. R.S. RATNA'
    },
    'Sustainable Development Goals': {
        centerName: 'RIS',
        coordinator: 'DR. SABYASACHI SAHA'
    },
    'WTO | IPR | BITS | Govt Procurement': {
        centerName: 'RIS',
        coordinator: 'DR. ATUL KAUSHIK'
    },
    'Standards': {
        centerName: 'RIS',
        coordinator: 'MR. ANIL JAUHRI'
    },
    'Trade Facilitation': {
        centerName: 'RIS',
        coordinator: 'DR. PRABIR DE'
    },
    'Sustainability': {
        centerName: 'RIS',
        coordinator: 'Ms. Lakshmi Swathi | Dr. Atul Kaushik | Dr. Sachin Kumar Sharma'
    },
    'Trade and Technology': {
        centerName: 'RIS',
        coordinator: 'DR. SABYASACHI SAHA'
    },
    'Industry & Digital trade': {
        centerName: 'RIS',
        coordinator: 'DR. PANKAJ VASHISHT'
    },
    'FTAs & Critical Minerals': {
        centerName: 'RIS',
        coordinator: 'Dr. Sachin Kumar Sharma | Dr. R.S. Ratna | Dr. Pankaj Vashisht'
    },
    'CMEC': {
        centerName: 'RIS + CMEC',
        coordinator: 'DR. SHISHIR SHROTRIYA'
    },
    'Connectivity': {
        centerName: 'RIS + CMEC',
        coordinator: 'DR. PRABIR DE'
    },
    'Blue Economy': {
        centerName: 'RIS + CMEC',
        coordinator: 'DR. PANKHURI GAUR'
    },
    'Maritime Connectivity': {
        centerName: 'RIS + CMEC',
        coordinator: 'CMDRE. SUJEET SAMADDAR'
    },
    'Finance': {
        centerName: 'RIS',
        coordinator: 'DR. PRIYADARSHI DASH'
    },
    'Forum on Indian Traditional Medicine': {
        centerName: 'FITM',
        coordinator: 'DR. SARIN'
    },
    'Health (DAKSHIN)': {
        centerName: 'RIS + DAKSHIN',
        coordinator: 'DR. MONIKA KOCHHAR'
    },
    'Science and Diplomacy': {
        centerName: 'RIS',
        coordinator: 'DR. AMIT KUMAR'
    },
    'AI | Digital (DAKSHIN)': {
        centerName: 'RIS + DAKSHIN',
        coordinator: 'DAKSHIN Advisor'
    },
    'Agriculture': {
        centerName: 'RIS + DAKSHIN',
        coordinator: 'DR. P S BIRTHAL'
    },
    'Trade and Agriculture': {
        centerName: 'RIS + DAKSHIN',
        coordinator: 'DR. SACHIN KUMAR SHARMA'
    }
};

const COORDINATOR_OPTIONS = [
    'DR. MONIKA KOCHHAR',
    'DR. SUSHIL KUMAR',
    'DR. SABYASACHI SAHA',
    'DR. PRABIR DE',
    'DR. AMIT KUMAR',
    'DR. PRIYADARSHI DASH',
    'DR. P S BIRTHAL',
    'MR. ANIL JAUHRI',
    'DR. R.S. RATNA',
    'CMDRE. SUJEET SAMADDAR',
    'DR. ATUL KAUSHIK',
    'DR. PANKAJ VASHISHT',
    'DR. SHISHIR SHROTRIYA',
    'DR. PANKHURI GAUR',
    'DR. SARIN',
    'DR. SACHIN KUMAR SHARMA',
    'DAKSHIN Advisor',
    'Other'
];


const EVENT_TYPE_OPTIONS = [
    'CONFERENCE',
    'SEMINAR',
    'WORKSHOP',
    'EXPERT CONSULTATION',
    'MEETING',
    'LECTURE',
    'PANEL DISCUSSION',
    'OTHER'
];

const STATUS_OPTIONS = [
    'Scheduled',
    'Pending',
    'Done',
    'Cancelled'
];

const EventPlanner = () => {
    // Auth & role detection
    const { user } = useAuth();
    const isDG = user?.roles?.some(r => r === 'DG' || r === 'DG_OFFICE') ?? false;
    const currentUsername = user?.username || 'Staff';

    // Standardized current date for checking 'Today' cell in Asia/Kolkata timezone
    const todayDateStr = useMemo(() => {
        const today = new Date();
        // Shift date to IST timezone to match backend events normalization
        const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' });
        return formatter.format(today);
    }, []);

    // Edit permission check: DG always unlocked; others if not locked and they are the creator
    const isEditWindowOpen = (evt) => {
        if (isDG) return true;
        if (evt.isLocked) return false;

        const currentUserId = user?.userId || user?.sub || currentUsername;
        const creatorId = evt.CreatedBy || evt.createdBy;

        if (!creatorId || !currentUserId) return true;
        return String(creatorId) === String(currentUserId);
    };

    // Navigation / View Tab state
    const [activeTab, setActiveTab] = useState('spreadsheet'); // 'spreadsheet' | 'calendar'
    const [dgEditingRowId, setDgEditingRowId] = useState(null); // row currently being edited by DG

    // Comment panel state
    const [commentEventId, setCommentEventId] = useState(null); // which event's comments are open
    const [commentDraft, setCommentDraft] = useState('');
    const commentEndRef = useRef(null);
    const importFileRef = useRef(null); // hidden file input for CSV import

    const queryClient = useQueryClient();

    const { data: dbEventsData } = useQuery({
        queryKey: ['planner_events'],
        queryFn: () => eventService.getPlannerEvents(),
        staleTime: 30000,
        enabled: !!user
    });

    const saveEventToDb = async (updatedEvent) => {
        if (!updatedEvent) return;
        try {
            await eventService.updatePlanner(updatedEvent.id || updatedEvent.EventID, updatedEvent);
            queryClient.invalidateQueries({ queryKey: ['planner_events'] });
        } catch (e) {
            console.error("Failed to save event to DB", e);
            alert("Failed to save changes to server: " + e.message);
        }
    };

    const handleCellBlur = async (id) => {
        const evt = events.find(e => e.id === id || e.EventID === id);
        if (evt) {
            await saveEventToDb(evt);
        }
    };

    const [events, setEvents] = useState([]);

    useEffect(() => {
        if (dbEventsData) {
            const mapped = dbEventsData.map(evt => {
                const cleaned = { ...evt };
                cleaned.id = cleaned.EventID || cleaned.id;
                if (cleaned.mode === 'ONLINE/OFFLINE/HYBRID') cleaned.mode = 'HYBRID';
                if (cleaned.verticalName === 'Health') cleaned.verticalName = 'HEALTH';
                if (cleaned.centerName === 'DAKSHIN') cleaned.centerName = 'RIS + DAKSHIN';
                if (cleaned.coordinator === 'DR MONIKA' || cleaned.coordinator === 'DR. MONIKA KOCHAR') {
                    cleaned.coordinator = 'DR. MONIKA KOCHHAR';
                }
                if (!cleaned.subVertical) cleaned.subVertical = SUB_VERTICAL_OPTIONS[cleaned.verticalName]?.[0] || '';
                if (!cleaned.comments) cleaned.comments = [];
                return cleaned;
            });
            
            setEvents(prev => {
                // If prev is empty (initial load), sort initially by date
                if (!prev || prev.length === 0) {
                    mapped.sort((a, b) => {
                        const da = a.date || '';
                        const db = b.date || '';
                        if (da < db) return sortOrder === 'asc' ? -1 : 1;
                        if (da > db) return sortOrder === 'asc' ?  1 : -1;
                        return (a.sno || 0) - (b.sno || 0);
                    });
                    return mapped;
                }
                
                // If prev already has events, merge updates in-place to maintain the same row indexes!
                const updatedList = prev.map(existing => {
                    const serverCopy = mapped.find(m => m.id === existing.id);
                    return serverCopy ? serverCopy : existing;
                });
                
                // Prepend any newly created items that aren't in prev to the top
                const newItems = mapped.filter(m => !prev.some(existing => existing.id === m.id));
                return [...newItems, ...updatedList];
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbEventsData]);

    const handleSortToggle = () => {
        const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(nextOrder);
        setEvents(prev => {
            const sorted = [...prev].sort((a, b) => {
                const da = a.date || '';
                const db = b.date || '';
                if (da < db) return nextOrder === 'asc' ? -1 : 1;
                if (da > db) return nextOrder === 'asc' ?  1 : -1;
                return (a.sno || 0) - (b.sno || 0);
            });
            return sorted;
        });
    };

    const handleDgSaveRow = async (id) => {
        let evt = null;
        if (activeEventDetail && activeEventDetail.id === id) {
            evt = activeEventDetail;
        } else {
            evt = events.find(e => e.id === id || e.EventID === id);
        }
        
        if (evt) {
            await saveEventToDb(evt);
        }
        setDgEditingRowId(null);
    };


    // Calendar state
    const [currentCalDate, setCurrentCalDate] = useState(new Date(2026, 5, 1)); // June 2026 for demo alignment
    const [selectedCalDay, setSelectedCalDay] = useState(null);
    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [verticalFilter, setVerticalFilter] = useState('ALL');
    const [dateFrom, setDateFrom] = useState('');   // YYYY-MM-DD or ''
    const [dateTo, setDateTo] = useState('');         // YYYY-MM-DD or ''
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
    const [showOnlyComments, setShowOnlyComments] = useState(false);
    const [showOnlyConflicts, setShowOnlyConflicts] = useState(false);
    const [showArchived, setShowArchived] = useState(false);

    // Auto-update status to 'Done' and auto-archive events older than 1 day in Asia/Kolkata timezone
    useEffect(() => {
        if (!events || events.length === 0) return;
        const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        const todayMs = new Date(todayStr + 'T00:00:00').getTime();
        const oneDayMs = 24 * 60 * 60 * 1000;

        const needsUpdate = events.some(e => {
            if (!e.date || e.status === 'Cancelled' || e.isArchived) return false;
            const eMs = new Date(e.date + 'T00:00:00').getTime();
            return (todayMs - eMs > oneDayMs) && (e.status !== 'Done' || !e.isArchived);
        });

        if (needsUpdate) {
            setEvents(prev => prev.map(e => {
                if (!e.date || e.status === 'Cancelled' || e.isArchived) return e;
                const eMs = new Date(e.date + 'T00:00:00').getTime();
                if (todayMs - eMs > oneDayMs) {
                    return { ...e, status: 'Done', isArchived: true };
                }
                return e;
            }));
        }
    }, [events]);

    // Detail Popover / Modal state (for calendar edit/details view)
    const [activeEventDetail, setActiveEventDetail] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    // Double-booking conflict detector based on overlapping time ranges
    const conflictIdsSet = useMemo(() => {
        const set = new Set();
        // Compare every pair of active events
        for (let i = 0; i < events.length; i++) {
            const a = events[i];
            if (!a.date || !a.time || !a.venue || a.status === 'Cancelled') continue;
            const aVenue = a.venue.trim().toLowerCase();
            if (aVenue === 'other' || aVenue === 'type manually') continue;
            
            const aStart = a.date;
            const aEnd = a.toDate || aStart;
            
            for (let j = i + 1; j < events.length; j++) {
                const b = events[j];
                if (!b.date || !b.time || !b.venue || b.status === 'Cancelled') continue;
                
                const bStart = b.date;
                const bEnd = b.toDate || bStart;
                
                // If date ranges do not overlap, there is no conflict
                if (aStart > bEnd || bStart > aEnd) continue;
                
                const bVenue = b.venue.trim().toLowerCase();
                if (bVenue === 'other' || bVenue === 'type manually') continue;
                if (aVenue !== bVenue) continue;
                
                // If they overlap
                if (timeRangesOverlap(a.time, b.time)) {
                    set.add(a.id);
                    set.add(b.id);
                }
            }
        }
        return set;
    }, [events]);

    const hasConflict = useCallback((evt) => {
        if (!evt || !evt.id) return false;
        return conflictIdsSet.has(evt.id);
    }, [conflictIdsSet]);

    // Custom select styling to prevent dropdown caret overlap
    const getSelectStyle = (field, value) => {
        const colorStyles = getCellColorStyle(field, value);
        return {
            width: '100%',
            background: 'transparent',
            backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 2px center',
            backgroundSize: '10px',
            paddingRight: '14px',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
            border: 'none',
            outline: 'none',
            fontSize: '0.76rem',
            fontFamily: 'inherit',
            color: value ? 'inherit' : '#94a3b8',
            cursor: 'pointer',
            fontWeight: '500',
            ...colorStyles
        };
    };

    // Reusable select dropdown with "Other" text input fallback for Details Modal
    const renderModalSelect = (field, value, options, backgroundStyle = {}, disabled = false) => {
        const hasValue = value && value !== '';
        const isOther = value === 'Other' || value === 'OTHER' || value === 'Type manually' || value === 'TYPE MANUALLY';
        const isValueInOptions = options.includes(value);
        const isCustom = isOther || (hasValue && !isValueInOptions);

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', width: '100%' }}>
                <select
                    value={value || ''}
                    onChange={(e) => handleCellChange(activeEventDetail.id, field, e.target.value)}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        padding: '0.6rem 2rem 0.6rem 0.75rem',
                        borderRadius: '8px',
                        border: disabled ? '1px solid #e2e8f0' : '1px solid #cbd5e1',
                        background: disabled ? '#f8fafc' : (backgroundStyle.backgroundColor || 'white'),
                        color: disabled ? '#64748b' : (backgroundStyle.color || '#334155'),
                        fontWeight: disabled ? '500' : (backgroundStyle.fontWeight || '600'),
                        fontSize: '0.82rem',
                        outline: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        backgroundSize: '12px',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        boxSizing: 'border-box'
                    }}
                >
                    <option value="" style={{ color: '#94a3b8' }}>— Select —</option>
                    {options.map(opt => (
                        <option key={opt} value={opt} style={{ background: '#fff', color: '#333' }}>{opt}</option>
                    ))}
                    {hasValue && !isValueInOptions && !isOther && (
                        <option value={value} style={{ background: '#fff', color: '#333' }}>{value}</option>
                    )}
                </select>

                {isCustom && !disabled && (
                    <input
                        type="text"
                        value={isOther ? '' : value}
                        onChange={(e) => handleCellChange(activeEventDetail.id, field, e.target.value || (field === 'venue' ? 'Type manually' : 'Other'))}
                        onBlur={() => handleCellBlur(activeEventDetail.id)}
                        placeholder="Type custom option…"
                        style={{
                            width: '100%',
                            padding: '0.6rem 0.75rem',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            background: '#ffffff',
                            color: '#334155',
                            fontSize: '0.82rem',
                            outline: 'none',
                            boxSizing: 'border-box'
                        }}
                    />
                )}
            </div>
        );
    };

    // Handle updates to specific cells
    const handleCellChange = (id, field, value) => {
        let updatedEvt = null;
        setEvents(prev => prev.map(evt => {
            if (evt.id === id) {
                updatedEvt = { ...evt, [field]: value };
                
                // Adjust toDate and proposedMonth if date changes
                if (field === 'date') {
                    if (!evt.toDate || evt.toDate < value) {
                        updatedEvt.toDate = value;
                    }
                    if (value) {
                        const parts = value.split('-');
                        if (parts.length === 3) {
                            const monthIdx = parseInt(parts[1], 10) - 1;
                            const MONTH_NAMES = [
                                'January', 'February', 'March', 'April', 'May', 'June',
                                'July', 'August', 'September', 'October', 'November', 'December'
                            ];
                            if (monthIdx >= 0 && monthIdx < 12) {
                                updatedEvt.proposedMonth = MONTH_NAMES[monthIdx];
                            }
                        }
                    }
                }

                // Clear Sub-vertical if Vertical changed and current Sub-vertical is not compatible
                if (field === 'verticalName') {
                    const compatibleOptions = SUB_VERTICAL_OPTIONS[value] || ['Other'];
                    if (!compatibleOptions.includes(evt.subVertical)) {
                        updatedEvt.subVertical = compatibleOptions[0];
                    }
                    // Cascade default center & coordinator for the chosen sub-vertical
                    const defaults = SUB_VERTICAL_DEFAULTS[updatedEvt.subVertical];
                    if (defaults) {
                        updatedEvt.centerName = defaults.centerName;
                        updatedEvt.coordinator = defaults.coordinator;
                    }
                } else if (field === 'subVertical') {
                    // Refine default center & coordinator when sub-vertical is selected
                    const defaults = SUB_VERTICAL_DEFAULTS[value];
                    if (defaults) {
                        updatedEvt.centerName = defaults.centerName;
                        updatedEvt.coordinator = defaults.coordinator;
                    }
                }
                return updatedEvt;
            }
            return evt;
        }));

        // If editing detail modal is active, update it too
        if (activeEventDetail && activeEventDetail.id === id) {
            setActiveEventDetail(prev => {
                const updated = { ...prev, [field]: value };
                
                if (field === 'date') {
                    if (!prev.toDate || prev.toDate < value) {
                        updated.toDate = value;
                    }
                    if (value) {
                        const parts = value.split('-');
                        if (parts.length === 3) {
                            const monthIdx = parseInt(parts[1], 10) - 1;
                            const MONTH_NAMES = [
                                'January', 'February', 'March', 'April', 'May', 'June',
                                'July', 'August', 'September', 'October', 'November', 'December'
                            ];
                            if (monthIdx >= 0 && monthIdx < 12) {
                                updated.proposedMonth = MONTH_NAMES[monthIdx];
                            }
                        }
                    }
                }

                if (field === 'verticalName') {
                    const compatibleOptions = SUB_VERTICAL_OPTIONS[value] || ['Other'];
                    if (!compatibleOptions.includes(prev.subVertical)) {
                        updated.subVertical = compatibleOptions[0];
                    }
                    // Cascade default center & coordinator for the chosen sub-vertical
                    const defaults = SUB_VERTICAL_DEFAULTS[updated.subVertical];
                    if (defaults) {
                        updated.centerName = defaults.centerName;
                        updated.coordinator = defaults.coordinator;
                    }
                } else if (field === 'subVertical') {
                    // Refine default center & coordinator when sub-vertical is selected
                    const defaults = SUB_VERTICAL_DEFAULTS[value];
                    if (defaults) {
                        updated.centerName = defaults.centerName;
                        updated.coordinator = defaults.coordinator;
                    }
                }
                return updated;
            });
        }

        // Save immediate fields
        const immediateFields = [
            'verticalName', 'subVertical', 'centerName', 'coordinator', 
            'eventType', 'mode', 'status', 'date', 'toDate', 'time', 'isLocked', 'isArchived'
        ];
        if (immediateFields.includes(field) && updatedEvt) {
            saveEventToDb(updatedEvt);
        }
    };

    // Add a new row to the planner (DG or standard user)
    const handleAddRow = async (defaultDate = '') => {
        const nextSno = events.length > 0 ? Math.max(...events.map(e => e.sno || 0)) + 1 : 1;
        const eventId = `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const userDivision = user?.attributes?.division || user?.roles?.find(r => r.startsWith('COORDINATOR_'))?.split('_').slice(1).join('_') || 'Other';
        const newEvt = {
            id: eventId,
            EventID: eventId,
            isPlannerEvent: true,
            sno: nextSno,
            Division: userDivision,
            title: '',
            venue: '',
            mode: '',
            date: defaultDate || '',
            toDate: defaultDate || '',
            proposedMonth: '',
            time: '12:00 - 12:00',
            verticalName: '',
            subVertical: '',
            centerName: '',
            coordinator: '',
            eventType: '',
            theme: '',
            participants: '',
            status: 'Scheduled',
            partnerOrganisation: '',
            createdAt: new Date().toISOString(),
            CreatedBy: user?.userId || user?.sub || currentUsername,
            createdBy: user?.userId || user?.sub || currentUsername,
            createdByUsername: currentUsername,
            comments: []
        };
        try {
            await eventService.createPlanner(newEvt);
            queryClient.invalidateQueries({ queryKey: ['planner_events'] });
            setEvents(prev => [newEvt, ...prev]);
            if (isDG) {
                setDgEditingRowId(newEvt.id);
            }
            setHighlightedRowId(newEvt.id);
            setTimeout(() => setHighlightedRowId(null), 2500);
        } catch (e) {
            console.error("Failed to create row on server", e);
            alert("Failed to add row on server: " + e.message);
        }
    };

    // Delete a row (DG or anyone before locking/archiving)
    const handleDeleteRow = (id) => {
        const evt = events.find(e => e.id === id);
        const canDelete = isDG || (evt && !evt.isArchived && isEditWindowOpen(evt));
        if (!canDelete) {
            alert('You do not have permission to delete this event.');
            return;
        }
        setConfirmModal({
            type: 'delete',
            title: 'Delete Event',
            message: 'Are you sure you want to permanently delete this event? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    await eventService.deletePlanner(id);
                    queryClient.invalidateQueries({ queryKey: ['planner_events'] });
                    setEvents(prev => {
                        const filtered = prev.filter(e => e.id !== id);
                        return filtered.map((e, index) => ({ ...e, sno: index + 1 }));
                    });
                    if (activeEventDetail?.id === id) {
                        setActiveEventDetail(null);
                    }
                    if (commentEventId === id) setCommentEventId(null);
                    setConfirmModal(null);
                } catch (e) {
                    console.error("Failed to delete row on server", e);
                    alert("Failed to delete row on server: " + e.message);
                }
            }
        });
    };

    // Lock an event permanently for standard employees
    const handleLockEvent = (id) => {
        const evt = events.find(e => e.id === id);
        if (!evt) return;

        setConfirmModal({
            type: 'lock',
            title: 'Review & Lock Event',
            message: 'Please review all event details before locking. Once locked, this event will be read-only for standard users:',
            evtDetails: evt,
            onConfirm: async () => {
                const updatedEvt = { ...evt, isLocked: true };
                try {
                    await saveEventToDb(updatedEvt);
                    setEvents(prev => prev.map(e => e.id === id ? updatedEvt : e));
                    if (activeEventDetail && activeEventDetail.id === id) {
                        setActiveEventDetail(prev => ({ ...prev, isLocked: true }));
                    }
                    setConfirmModal(null);
                } catch (e) {
                    alert("Failed to lock event: " + e.message);
                }
            }
        });
    };

    const handleCloseModal = () => {
        setActiveEventDetail(null);
        if (isDG) {
            setDgEditingRowId(null);
        }
    };

    // Add a comment to an event
    const handleAddComment = async (eventId) => {
        const text = commentDraft.trim();
        if (!text) return;
        const newComment = {
            id: `c-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            text,
            authorName: user?.attributes?.name || user?.username || 'Staff',
            authorUsername: user?.username || 'unknown',
            postedAt: new Date().toISOString()
        };
        
        const existingEvt = events.find(evt => evt.id === eventId);
        if (!existingEvt) return;
        
        const updatedEvt = { ...existingEvt, comments: [...(existingEvt.comments || []), newComment] };
        
        setEvents(prev => prev.map(evt => evt.id === eventId ? updatedEvt : evt));
        setCommentDraft('');
        setTimeout(() => commentEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

        await saveEventToDb(updatedEvt);
    };

    // Export current planner as JSON
    // eslint-disable-next-line no-unused-vars
    const handleExportData = () => {
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(events, null, 2));
        const downloadAnchor = document.createElement('a');
        downloadAnchor.setAttribute("href", dataStr);
        downloadAnchor.setAttribute("download", "ris_events_planner_export.json");
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();
    };

    // Export current planner as Excel Compatible CSV (with UTF-8 BOM)
    const handleExportExcel = () => {
        const headers = [
            'Sno',
            'TITLE',
            'VENUE',
            'MODE',
            'DATE SCHEDULED',
            'END DATE SCHEDULED',
            'PROPOSED MONTH',
            'TIME',
            'VERTICAL NAME',
            'SUB VERTICAL',
            'CENTER NAME',
            'COORDINATOR / LEAD PERSON',
            'TYPE OF EVENT',
            'THEME',
            'EXPECTED PARTICIPANTS COUNT',
            'STATUS',
            'PARTNER ORGANISATION'
        ];

        const rows = events.map(e => [
            e.sno,
            e.title,
            e.venue,
            e.mode,
            e.date ? new Date(e.date).toLocaleDateString('en-GB') : '',
            e.toDate ? new Date(e.toDate).toLocaleDateString('en-GB') : (e.date ? new Date(e.date).toLocaleDateString('en-GB') : ''),
            e.proposedMonth || '',
            e.time,
            e.verticalName,
            e.subVertical,
            e.centerName,
            e.coordinator,
            e.eventType,
            e.theme,
            e.participants,
            e.status,
            e.partnerOrganisation
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(r => r.map(val => {
                const str = String(val === undefined || val === null ? '' : val);
                if (str.includes(',') || str.includes('\n') || str.includes('"')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            }).join(','))
        ].join('\n');

        const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        const downloadAnchor = document.createElement('a');
        downloadAnchor.setAttribute("href", url);
        downloadAnchor.setAttribute("download", "ris_event_planner.csv");
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();
        URL.revokeObjectURL(url);
    };

    // Import events from a CSV file (same format as Export to Excel)
    const handleImportCSV = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Reset input so same file can be re-imported if needed
        e.target.value = '';

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const text = evt.target.result
                    // Strip UTF-8 BOM if present
                    .replace(/^\uFEFF/, '');

                const lines = text.split(/\r?\n/).filter(l => l.trim());
                if (lines.length < 2) {
                    alert('The CSV file appears to be empty or has no data rows.');
                    return;
                }

                // Parse a single CSV line respecting quoted fields
                const parseCSVLine = (line) => {
                    const result = [];
                    let current = '';
                    let inQuotes = false;
                    for (let i = 0; i < line.length; i++) {
                        const ch = line[i];
                        if (ch === '"') {
                            if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
                            else inQuotes = !inQuotes;
                        } else if (ch === ',' && !inQuotes) {
                            result.push(current.trim()); current = '';
                        } else {
                            current += ch;
                        }
                    }
                    result.push(current.trim());
                    return result;
                };

                const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());

                // Map CSV header → event field
                const COL = {
                    sno:               headers.indexOf('sno'),
                    title:             headers.findIndex(h => h.includes('title')),
                    venue:             headers.findIndex(h => h.includes('venue')),
                    mode:              headers.findIndex(h => h.includes('mode')),
                    date:              headers.findIndex(h => h.includes('date') && !h.includes('end') && !h.includes('to')),
                    toDate:            headers.findIndex(h => h.includes('end date') || h.includes('to date')),
                    proposedMonth:     headers.findIndex(h => h.includes('proposed month') || h.includes('proposedmonth')),
                    time:              headers.findIndex(h => h === 'time'),
                    verticalName:      headers.findIndex(h => h.includes('vertical name') || h === 'vertical'),
                    subVertical:       headers.findIndex(h => h.includes('sub vertical') || h.includes('sub-vertical')),
                    centerName:        headers.findIndex(h => h.includes('center')),
                    coordinator:       headers.findIndex(h => h.includes('coordinator')),
                    eventType:         headers.findIndex(h => h.includes('type')),
                    theme:             headers.findIndex(h => h.includes('theme')),
                    participants:      headers.findIndex(h => h.includes('participant') || h.includes('count')),
                    status:            headers.findIndex(h => h.includes('status')),
                    partnerOrganisation: headers.findIndex(h => h.includes('partner')),
                };

                const getCol = (cols, idx) => (idx >= 0 ? (cols[idx] || '') : '');

                // Parse a dd/mm/yyyy or dd-mm-yyyy date back to YYYY-MM-DD for storage
                const parseDate = (str) => {
                    if (!str) return '';
                    str = str.trim();
                    // already ISO format YYYY-MM-DD
                    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
                    
                    // Support both / and - separators
                    const parts = str.split(/[-/]/);
                    if (parts.length === 3) {
                        // If year is the first part (yyyy/mm/dd)
                        if (parts[0].length === 4) {
                            return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
                        }
                        // Assume day/month/year or month/day/year. We prioritize day/month/year (en-GB format)
                        if (parts[2].length === 4 || parts[2].length === 2) {
                            let year = parts[2];
                            if (year.length === 2) {
                                year = '20' + year; // assume 21st century
                            }
                            const day = parts[0].padStart(2, '0');
                            const month = parts[1].padStart(2, '0');
                            return `${year}-${month}-${day}`;
                        }
                    }
                    return str;
                };

                const maxSno = events.length > 0 ? Math.max(...events.map(e => e.sno || 0)) : 0;

                const imported = lines.slice(1).map((line, idx) => {
                    const cols = parseCSVLine(line);
                    return {
                        id: `import-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 6)}`,
                        sno: parseInt(getCol(cols, COL.sno)) || (maxSno + idx + 1),
                        title:             getCol(cols, COL.title),
                        venue:             getCol(cols, COL.venue),
                        mode:              getCol(cols, COL.mode),
                        date:              parseDate(getCol(cols, COL.date)),
                        toDate:            parseDate(getCol(cols, COL.toDate)) || parseDate(getCol(cols, COL.date)),
                        proposedMonth:     getCol(cols, COL.proposedMonth),
                        time: (() => {
                            const rawTime = getCol(cols, COL.time);
                            const { start, end } = parseTimeRange(rawTime);
                            return formatTimeRange(start, end);
                        })(),
                        verticalName:      getCol(cols, COL.verticalName),
                        subVertical:       getCol(cols, COL.subVertical),
                        centerName:        getCol(cols, COL.centerName),
                        coordinator:       getCol(cols, COL.coordinator),
                        eventType:         getCol(cols, COL.eventType),
                        theme:             getCol(cols, COL.theme),
                        participants:      getCol(cols, COL.participants),
                        status:            getCol(cols, COL.status) || 'Scheduled',
                        partnerOrganisation: getCol(cols, COL.partnerOrganisation),
                        // Imported events belong to DG — mark with old timestamp so they're locked for employees
                        createdAt: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
                        comments: []
                    };
                }).filter(e => e.title || e.date); // skip truly blank rows

                if (imported.length === 0) {
                    alert('No valid rows found in the CSV file. Please check the file format.');
                    return;
                }

                const replace = window.confirm(
                    `Import ${imported.length} event(s) from "${file.name}"?\n\n` +
                    `• OK — REPLACE all existing events with imported data\n` +
                    `• Cancel — APPEND imported events to existing list`
                );

                setEvents(replace ? imported : (prev) => {
                    const maxS = prev.length > 0 ? Math.max(...prev.map(e => e.sno || 0)) : 0;
                    return [
                        ...prev,
                        ...imported.map((e, i) => ({ ...e, sno: maxS + i + 1 }))
                    ];
                });

            } catch (err) {
                console.error('CSV import error:', err);
                alert('Failed to parse the CSV file. Please ensure it was exported from this planner.');
            }
        };
        reader.readAsText(file, 'UTF-8');
    };
    const filteredEvents = useMemo(() => {
        const filtered = events.filter(evt => {
            const matchesSearch =
                (evt.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (evt.theme || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (evt.partnerOrganisation || '').toLowerCase().includes(searchQuery.toLowerCase());

            const matchesStatus = statusFilter === 'ALL' || evt.status === statusFilter;
            const matchesVertical = verticalFilter === 'ALL' || evt.verticalName === verticalFilter;
            
            const start = evt.date || '';
            const end = evt.toDate || start;

            const matchesCalDay = activeTab === 'calendar' || !selectedCalDay || (selectedCalDay >= start && selectedCalDay <= end);

            // Date range filtering (overlap check)
            const matchesFrom = !dateFrom || end >= dateFrom;
            const matchesTo   = !dateTo   || start <= dateTo;

            const matchesComments = !showOnlyComments || (evt.comments && evt.comments.length > 0);
            const matchesConflicts = !showOnlyConflicts || hasConflict(evt);
            const matchesArchive = showArchived ? (evt.isArchived === true) : (!evt.isArchived);

            return matchesSearch && matchesStatus && matchesVertical && matchesCalDay && matchesFrom && matchesTo && matchesComments && matchesConflicts && matchesArchive;
        });

        return filtered;
    }, [events, searchQuery, statusFilter, verticalFilter, selectedCalDay, activeTab, dateFrom, dateTo, showOnlyComments, showOnlyConflicts, hasConflict, showArchived]);

    // Calendar Helper Utilities
    const currentYear = currentCalDate.getFullYear();
    const currentMonth = currentCalDate.getMonth();

    const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

    const nextMonth = () => {
        setCurrentCalDate(new Date(currentYear, currentMonth + 1, 1));
    };

    const prevMonth = () => {
        setCurrentCalDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const calendarDays = useMemo(() => {
        const totalDays = getDaysInMonth(currentYear, currentMonth);
        const paddingDays = getFirstDayOfMonth(currentYear, currentMonth);
        const days = [];

        for (let i = 0; i < paddingDays; i++) {
            days.push({ isPadding: true, dayNum: null });
        }

        for (let d = 1; d <= totalDays; d++) {
            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const dayEvents = filteredEvents.filter(e => {
                const start = e.date || '';
                const end = e.toDate || start;
                return dateStr >= start && dateStr <= end;
            });
            days.push({
                isPadding: false,
                dayNum: d,
                dateStr,
                events: dayEvents
            });
        }

        return days;
    }, [filteredEvents, currentYear, currentMonth]);

    const monthName = currentCalDate.toLocaleString('default', { month: 'long' });

    // Cell color codes matching user screenshot
    const getCellColorStyle = (field, value) => {
        const greenFields = ['venue', 'mode', 'verticalName', 'subVertical', 'centerName', 'coordinator', 'eventType', 'status'];
        const yellowFields = ['title', 'theme', 'partnerOrganisation', 'time'];
        const redFields = ['participants'];

        if (greenFields.includes(field)) {
            if (field === 'status') {
                if (value === 'Done') return { backgroundColor: '#e2f0d9', color: '#385723', fontWeight: '700' };
                if (value === 'Cancelled') return { backgroundColor: '#f8cbad', color: '#c65911', fontWeight: '600' };
                if (value === 'In Progress') return { backgroundColor: '#fff2cc', color: '#7f6000', fontWeight: '700' };
                return { backgroundColor: '#e2f0d9', color: '#385723' };
            }
            return { backgroundColor: '#e2f0d9', color: '#385723' }; 
        }
        if (yellowFields.includes(field)) {
            return { backgroundColor: '#fff2cc', color: '#7f6000' }; 
        }
        if (redFields.includes(field)) {
            return { backgroundColor: '#ffc7ce', color: '#9c0006', fontWeight: 'bold', textAlign: 'center' }; 
        }
        return {};
    };

    // Cohesive status coloring themes for Specialized Calendar View
    const getStatusTheme = (status) => {
        switch (status) {
            case 'Done':
                return { bg: '#e2f0d9', border: '#385723', text: '#385723' };
            case 'Cancelled':
                return { bg: '#f8cbad', border: '#c65911', text: '#c65911' };
            case 'In Progress':
                return { bg: '#fff2cc', border: '#7f6000', text: '#7f6000' };
            case 'Scheduled':
                return { bg: '#e0f2fe', border: '#0284c7', text: '#0369a1' };
            case 'Pending':
            default:
                return { bg: '#f3e8ff', border: '#a855f7', text: '#6b21a8' };
        }
    };

    // Jump from calendar click direct to row in spreadsheet tab
    const handleJumpToRow = (evt) => {
        setSelectedCalDay(evt.date);
        setActiveTab('spreadsheet');
        setHighlightedRowId(evt.id);
        setTimeout(() => {
            const tableRow = document.getElementById(`row-${evt.id}`);
            const container = document.getElementById('spreadsheet-grid-scroll-container');
            if (tableRow && container) {
                const rowTop = tableRow.offsetTop;
                const containerHeight = container.clientHeight;
                const rowHeight = tableRow.clientHeight;
                container.scrollTo({
                    top: rowTop - (containerHeight / 2) + (rowHeight / 2),
                    behavior: 'smooth'
                });
            }
        }, 100);
        setTimeout(() => setHighlightedRowId(null), 2500);
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}>
            {/* Top Header Card */}
            <div style={{
                background: 'linear-gradient(135deg, #eae6f8 0%, #dbe9f6 50%, #d2f3eb 100%)',
                borderRadius: '12px',
                padding: '0.6rem 1.25rem',
                color: '#1e3a8a',
                boxShadow: '0 4px 15px rgba(30, 41, 59, 0.05)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                border: '1px solid rgba(226, 232, 240, 0.8)'
            }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1e3a8a', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileSpreadsheet size={18} style={{ color: '#7c3aed' }} /> RIS Event Planner
                    </h1>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {/* Hidden file input for CSV import */}
                    <input
                        ref={importFileRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleImportCSV}
                        style={{ display: 'none' }}
                    />

                    {/* Import CSV */}
                    <button
                        onClick={() => importFileRef.current?.click()}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.35rem',
                            padding: '0.35rem 0.75rem', borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            background: 'rgba(255,255,255,0.6)', color: '#334155',
                            cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.9)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.6)'}
                        title="Import events from a CSV file (same format as Export to Excel)"
                    >
                        <Upload size={13} style={{ color: '#475569' }} /> Import CSV
                    </button>

                    {/* Export CSV */}
                    <button
                        onClick={handleExportExcel}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.35rem',
                            padding: '0.35rem 0.75rem', borderRadius: '6px', border: 'none',
                            background: '#10b981', color: 'white', cursor: 'pointer',
                            fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
                    >
                        <Download size={13} /> Export CSV
                    </button>
                </div>
            </div>

            {/* DG Overview KPI Stats Bar */}
            {isDG && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    width: '100%'
                }}>
                    {/* Card 1: Total Events */}
                    <div 
                        onClick={() => {
                            setShowOnlyComments(false);
                            setShowOnlyConflicts(false);
                            setDateFrom('');
                            setDateTo('');
                            setSearchQuery('');
                            setStatusFilter('ALL');
                            setVerticalFilter('ALL');
                        }}
                        style={{
                            background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                            borderRadius: '12px',
                            padding: '1.1rem 1.4rem',
                            color: '#0c4a6e',
                            boxShadow: '0 4px 15px rgba(14, 165, 233, 0.08)',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            border: (!showOnlyComments && !showOnlyConflicts && !dateFrom) ? '2px solid #0284c7' : '2px solid transparent'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(14, 165, 233, 0.18)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(14, 165, 233, 0.08)';
                        }}
                        title="Click to view all events and reset filters"
                    >
                        <div>
                            <div style={{ fontSize: '0.74rem', fontWeight: 650, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Events</div>
                            <div style={{ fontSize: '1.75rem', fontWeight: 800, marginTop: '0.25rem' }}>{events.length}</div>
                        </div>
                        <FileSpreadsheet size={28} style={{ color: '#0284c7', opacity: 0.85 }} />
                    </div>

                    {/* Card 2: Upcoming Events */}
                    <div 
                        onClick={() => {
                            const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                            setDateFrom(todayStr);
                            setDateTo('');
                            setShowOnlyComments(false);
                            setShowOnlyConflicts(false);
                        }}
                        style={{
                            background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                            borderRadius: '12px',
                            padding: '1.1rem 1.4rem',
                            color: '#064e3b',
                            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.08)',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            border: (dateFrom === new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })) ? '2px solid #059669' : '2px solid transparent'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.18)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.08)';
                        }}
                        title="Click to view events scheduled today onwards"
                    >
                        <div>
                            <div style={{ fontSize: '0.74rem', fontWeight: 650, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Upcoming</div>
                            <div style={{ fontSize: '1.75rem', fontWeight: 800, marginTop: '0.25rem' }}>
                                {events.filter(e => e.date && getDaysDifference(e.toDate || e.date) >= 0).length}
                            </div>
                        </div>
                        <CalendarDays size={28} style={{ color: '#059669', opacity: 0.85 }} />
                    </div>

                    {/* Card 3: Double Bookings */}
                    <div 
                        onClick={() => {
                            setShowOnlyConflicts(prev => !prev);
                            setShowOnlyComments(false);
                        }}
                        style={{
                            background: 'linear-gradient(135deg, #ffe4e6 0%, #fecdd3 100%)',
                            borderRadius: '12px',
                            padding: '1.1rem 1.4rem',
                            color: '#4c0519',
                            boxShadow: '0 4px 15px rgba(244, 63, 94, 0.08)',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            border: showOnlyConflicts ? '2px solid #e11d48' : '2px solid transparent'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(244, 63, 94, 0.18)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(244, 63, 94, 0.08)';
                        }}
                        title="Click to toggle viewing double-booked schedule conflicts"
                    >
                        <div>
                            <div style={{ fontSize: '0.74rem', fontWeight: 650, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Double Bookings</div>
                            <div style={{ fontSize: '1.75rem', fontWeight: 800, marginTop: '0.25rem' }}>
                                {events.filter(e => hasConflict(e)).length}
                            </div>
                        </div>
                        <AlertTriangle size={28} style={{ color: '#e11d48', opacity: 0.85 }} />
                    </div>

                    {/* Card 4: Change Requests */}
                    <div 
                        onClick={() => {
                            setShowOnlyComments(prev => !prev);
                            setShowOnlyConflicts(false);
                        }}
                        style={{
                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            borderRadius: '12px',
                            padding: '1.1rem 1.4rem',
                            color: '#451a03',
                            boxShadow: '0 4px 15px rgba(249, 115, 22, 0.08)',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            border: showOnlyComments ? '2px solid #d97706' : '2px solid transparent'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(249, 115, 22, 0.18)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(249, 115, 22, 0.08)';
                        }}
                        title="Click to toggle viewing events with comment requests"
                    >
                        <div>
                            <div style={{ fontSize: '0.74rem', fontWeight: 650, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Change Requests</div>
                            <div style={{ fontSize: '1.75rem', fontWeight: 800, marginTop: '0.25rem' }}>
                                {events.filter(e => e.comments && e.comments.length > 0).length}
                            </div>
                        </div>
                        <MessageSquare size={28} style={{ color: '#d97706', opacity: 0.85 }} />
                    </div>
                </div>
            )}

            {/* Tab Selector Bar with Archive Button */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                marginTop: '0.25rem',
                flexWrap: 'wrap',
                gap: '0.75rem'
            }}>
                <div style={{
                    display: 'flex',
                    background: '#e2e8f0',
                    padding: '4px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1'
                }}>
                    <button
                        onClick={() => setActiveTab('spreadsheet')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            padding: '0.45rem 1.1rem',
                            border: 'none',
                            borderRadius: '7px',
                            cursor: 'pointer',
                            background: activeTab === 'spreadsheet' ? 'white' : 'transparent',
                            color: activeTab === 'spreadsheet' ? '#0f172a' : '#64748b',
                            fontWeight: activeTab === 'spreadsheet' ? 700 : 500,
                            fontSize: '0.82rem',
                            boxShadow: activeTab === 'spreadsheet' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                            transition: 'all 0.2s'
                        }}
                    >
                        <FileSpreadsheet size={15} /> Spreadsheet Grid
                    </button>
                    <button
                        onClick={() => setActiveTab('calendar')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            padding: '0.45rem 1.1rem',
                            border: 'none',
                            borderRadius: '7px',
                            cursor: 'pointer',
                            background: activeTab === 'calendar' ? 'white' : 'transparent',
                            color: activeTab === 'calendar' ? '#0f172a' : '#64748b',
                            fontWeight: activeTab === 'calendar' ? 700 : 500,
                            fontSize: '0.82rem',
                            boxShadow: activeTab === 'calendar' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                            transition: 'all 0.2s'
                        }}
                    >
                        <CalendarDays size={15} /> Specialized Calendar View
                    </button>
                </div>

                <button
                    onClick={() => setShowArchived(prev => !prev)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.45rem',
                        padding: '0.5rem 1.1rem',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        background: showArchived ? '#f1f5f9' : 'white',
                        color: showArchived ? '#0f172a' : '#475569',
                        borderColor: showArchived ? '#94a3b8' : '#cbd5e1',
                        cursor: 'pointer',
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = showArchived ? '#e2e8f0' : '#f8fafc';
                        e.currentTarget.style.borderColor = '#94a3b8';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = showArchived ? '#f1f5f9' : 'white';
                        e.currentTarget.style.borderColor = showArchived ? '#94a3b8' : '#cbd5e1';
                    }}
                >
                    <Archive size={15} style={{ color: showArchived ? '#0f172a' : '#475569' }} />
                    {showArchived ? 'View Active Events' : 'View Archived Events'}
                </button>
            </div>

            {/* Filter controls row */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'nowrap',
                gap: '0.75rem',
                background: 'white',
                borderRadius: '12px',
                padding: '0.75rem 1.25rem',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
            }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
                    {/* Search */}
                    <input
                        type="text"
                        placeholder="Search title, theme or partner…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            padding: '0.45rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1',
                            fontSize: '0.82rem', width: '220px', outline: 'none', transition: 'all 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                    />

                    {/* Date From */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>From</span>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            style={{
                                padding: '0.42rem 0.5rem', borderRadius: '6px',
                                border: dateFrom ? '1px solid #3b82f6' : '1px solid #cbd5e1',
                                fontSize: '0.8rem', outline: 'none', color: '#334155',
                                background: dateFrom ? '#eff6ff' : 'white'
                            }}
                        />
                    </div>

                    {/* Date To */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>To</span>
                        <input
                            type="date"
                            value={dateTo}
                            min={dateFrom || undefined}
                            onChange={(e) => setDateTo(e.target.value)}
                            style={{
                                padding: '0.42rem 0.5rem', borderRadius: '6px',
                                border: dateTo ? '1px solid #3b82f6' : '1px solid #cbd5e1',
                                fontSize: '0.8rem', outline: 'none', color: '#334155',
                                background: dateTo ? '#eff6ff' : 'white'
                            }}
                        />
                    </div>

                    {/* Sort toggle */}
                    <button
                        onClick={handleSortToggle}
                        title={`Sort by date: ${sortOrder === 'asc' ? 'oldest first' : 'newest first'}`}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.3rem',
                            padding: '0.42rem 0.7rem', borderRadius: '6px',
                            border: '1px solid #cbd5e1', background: 'white',
                            color: '#475569', fontSize: '0.8rem', fontWeight: 600,
                            cursor: 'pointer', transition: 'all 0.15s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#1d4ed8'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                    >
                        {sortOrder === 'asc' ? '↑' : '↓'} Date
                    </button>

                    {/* Vertical Filter */}
                    <select
                        value={verticalFilter}
                        onChange={(e) => setVerticalFilter(e.target.value)}
                        style={{
                            padding: '0.45rem 0.6rem', borderRadius: '6px',
                            border: verticalFilter !== 'ALL' ? '1px solid #3b82f6' : '1px solid #cbd5e1',
                            fontSize: '0.82rem',
                            background: verticalFilter !== 'ALL' ? '#eff6ff' : 'white',
                            color: verticalFilter !== 'ALL' ? '#1d4ed8' : 'inherit',
                            cursor: 'pointer', outline: 'none'
                        }}
                    >
                        <option value="ALL">All Verticals</option>
                        {VERTICAL_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>

                    {/* Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{
                            padding: '0.45rem 0.6rem', borderRadius: '6px',
                            border: statusFilter !== 'ALL' ? '1px solid #3b82f6' : '1px solid #cbd5e1',
                            fontSize: '0.82rem',
                            background: statusFilter !== 'ALL' ? '#eff6ff' : 'white',
                            color: statusFilter !== 'ALL' ? '#1d4ed8' : 'inherit',
                            cursor: 'pointer', outline: 'none'
                        }}
                    >
                        <option value="ALL">All Statuses</option>
                        {STATUS_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>

                    {/* Change Requests Filter Checkbox */}
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: showOnlyComments ? '#1e3a8a' : '#475569',
                        background: showOnlyComments ? '#eff6ff' : 'transparent',
                        border: showOnlyComments ? '1px solid #bfdbfe' : '1px solid transparent',
                        borderRadius: '6px',
                        padding: '0.35rem 0.6rem',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.2s'
                    }}>
                        <input
                            type="checkbox"
                            checked={showOnlyComments}
                            onChange={(e) => {
                                setShowOnlyComments(e.target.checked);
                                if (e.target.checked) setShowOnlyConflicts(false);
                            }}
                            style={{ cursor: 'pointer', accentColor: '#1e3a8a' }}
                        />
                        <span>Change Requests</span>
                    </label>

                    {/* Conflicts Filter Checkbox */}
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: showOnlyConflicts ? '#e11d48' : '#475569',
                        background: showOnlyConflicts ? '#fff1f2' : 'transparent',
                        border: showOnlyConflicts ? '1px solid #fecdd3' : '1px solid transparent',
                        borderRadius: '6px',
                        padding: '0.35rem 0.6rem',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.2s'
                    }}>
                        <input
                            type="checkbox"
                            checked={showOnlyConflicts}
                            onChange={(e) => {
                                setShowOnlyConflicts(e.target.checked);
                                if (e.target.checked) setShowOnlyComments(false);
                            }}
                            style={{ cursor: 'pointer', accentColor: '#e11d48' }}
                        />
                        <span>Conflicts</span>
                    </label>

                    {/* Clear all filters */}
                    {(dateFrom || dateTo || searchQuery || statusFilter !== 'ALL' || verticalFilter !== 'ALL' || showOnlyComments || showOnlyConflicts) && (
                        <button
                            onClick={() => { 
                                setDateFrom(''); 
                                setDateTo(''); 
                                setSearchQuery(''); 
                                setStatusFilter('ALL'); 
                                setVerticalFilter('ALL'); 
                                setShowOnlyComments(false); 
                                setShowOnlyConflicts(false); 
                            }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.3rem',
                                padding: '0.42rem 0.7rem', borderRadius: '6px',
                                border: '1px solid #fecaca', background: '#fef2f2',
                                color: '#ef4444', fontSize: '0.78rem', fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            × Clear Filters
                        </button>
                    )}

                    {/* Calendar day filter indicator */}
                    {activeTab === 'spreadsheet' && selectedCalDay && (
                        <button
                            onClick={() => setSelectedCalDay(null)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.35rem',
                                padding: '0.45rem 0.75rem', borderRadius: '8px', border: '1px solid #fecaca',
                                background: '#fef2f2', color: '#ef4444', fontSize: '0.82rem',
                                cursor: 'pointer', fontWeight: 500
                            }}
                        >
                            Clear Day: {new Date(selectedCalDay + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </button>
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem', flexShrink: 0 }}>
                    {!showArchived && (
                        <button
                            onClick={() => handleAddRow(selectedCalDay)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.4rem',
                                padding: '0.5rem 1.1rem', borderRadius: '8px', border: 'none',
                                background: '#10b981', color: 'white', cursor: 'pointer',
                                fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.1)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                        >
                            <Plus size={15} /> Add Event Row
                        </button>
                    )}
                    <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap', marginTop: isDG ? '0.15rem' : '0rem' }}>
                        {filteredEvents.length} of {events.length} event{events.length !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>

            {/* Tab Contents */}
            <div style={{ flex: 1, minHeight: 0 }}>
                {activeTab === 'spreadsheet' ? (
                    /* Spreadsheet Grid */
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '1.25rem',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {showArchived && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem 1rem',
                                background: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                color: '#475569',
                                fontSize: '0.82rem',
                                fontWeight: 700,
                                marginBottom: '1rem'
                            }}>
                                📁 ARCHIVED EVENTS VIEW — READ ONLY
                            </div>
                        )}
                        <div 
                            id="spreadsheet-grid-scroll-container"
                            style={{
                                overflowX: 'auto',
                                border: '1px solid #eaecf0',
                                borderRadius: '12px'
                            }}
                        >
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '0.78rem',
                                textAlign: 'left',
                                tableLayout: 'fixed'
                            }}>
                                <thead>
                                    <tr style={{
                                        background: '#1e293b',
                                        color: 'white',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10
                                    }}>
                                        <th style={{ width: '45px', padding: '0.75rem 0.5rem', textAlign: 'center', borderRight: '1px solid #334155' }}>Sno</th>
                                        <th style={{ width: '115px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>DATE</th>
                                        <th style={{ width: '115px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>END DATE</th>
                                        <th style={{ width: '135px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>PROPOSED MONTH</th>
                                        <th style={{ width: '170px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>TIME</th>
                                        <th style={{ width: '220px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>TITLE</th>
                                        <th style={{ width: '130px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>VENUE</th>
                                        <th style={{ width: '95px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>MODE</th>
                                        <th style={{ width: '200px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>VERTICAL</th>
                                        <th style={{ width: '220px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>SUB VERTICAL</th>
                                        <th style={{ width: '140px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>CENTER</th>
                                        <th style={{ width: '130px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>COORDINATOR</th>
                                        <th style={{ width: '120px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>TYPE</th>
                                        <th style={{ width: '180px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>THEME</th>
                                        <th style={{ width: '85px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155', textAlign: 'center' }}>COUNT</th>
                                        <th style={{ width: '150px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>PARTNER ORG.</th>
                                        <th style={{ width: '100px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155' }}>STATUS</th>
                                        <th style={{ width: '60px', padding: '0.75rem 0.5rem', borderRight: '1px solid #334155', textAlign: 'center' }}>SAVE</th>
                                        <th style={{ width: '90px', padding: '0.75rem 0.5rem', textAlign: 'center' }}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEvents.length === 0 ? (
                                        <tr>
                                            <td colSpan="19" style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                                                No events found matching current criteria. Click <strong>Add Event Row</strong> to create one.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredEvents.map((evt, index) => {
                                            const isHighlighted = highlightedRowId === evt.id;
                                            const editable = showArchived ? false : (isDG ? (dgEditingRowId === evt.id) : isEditWindowOpen(evt));
                                            const commentCount = (evt.comments || []).length;
                                            const hasUnreadComments = commentCount > 0;

                                            // Read-only span renderer
                                            const roCell = (field, value, extra = {}) => (
                                                <span style={{
                                                    display: 'block',
                                                    padding: '0.3rem 0.2rem',
                                                    fontSize: '0.76rem',
                                                    fontFamily: 'inherit',
                                                    color: 'inherit',
                                                    lineHeight: '1.3',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    ...extra
                                                }}>{value || '\u2014'}</span>
                                            );

                                            // Grid clean select: renders dropdown normally.
                                            // If 'Other' or a custom value is selected, a text input is shown below the dropdown.
                                             const renderGridSelect = (field, value, options) => {
                                                 const hasValue = value && value !== '';
                                                 const isOther = value === 'Other' || value === 'OTHER' || value === 'Type manually' || value === 'TYPE MANUALLY';
                                                 const isValueInOptions = options.includes(value);
                                                 const isCustom = isOther || (hasValue && !isValueInOptions);
                                                 return (
                                                     <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100%' }}>
                                                         <select
                                                             value={value || ''}
                                                             onChange={(e) => handleCellChange(evt.id, field, e.target.value)}
                                                             style={getSelectStyle(field, value)}
                                                         >
                                                             <option value="" style={{ color: '#94a3b8' }}>— Select —</option>
                                                             {options.map(opt => (
                                                                 <option key={opt} value={opt} style={{ background: '#fff', color: '#333' }}>{opt}</option>
                                                             ))}
                                                             {hasValue && !isValueInOptions && !isOther && (
                                                                 <option value={value} style={{ background: '#fff', color: '#333' }}>{value}</option>
                                                             )}
                                                         </select>
                                                         {isCustom && (
                                                             <input
                                                                 type="text"
                                                                 value={isOther ? '' : value}
                                                                 onChange={(e) => handleCellChange(evt.id, field, e.target.value || (field === 'venue' ? 'Type manually' : 'Other'))}
                                                                 onBlur={() => handleCellBlur(evt.id)}
                                                                 placeholder="Type custom option…"
                                                                 style={{
                                                                     width: '100%',
                                                                     padding: '3px 6px',
                                                                     borderRadius: '4px',
                                                                     border: '1px solid #cbd5e1',
                                                                     background: '#ffffff',
                                                                     color: '#334155',
                                                                     fontSize: '0.72rem',
                                                                     outline: 'none',
                                                                     boxSizing: 'border-box'
                                                                 }}
                                                             />
                                                         )}
                                                     </div>
                                                 );
                                             };

                                            return (
                                                <tr
                                                    id={`row-${evt.id}`}
                                                    key={evt.id}
                                                    style={{
                                                        borderBottom: '1px solid #eaecf0',
                                                        backgroundColor: isHighlighted ? '#f0f9ff' : 'transparent',
                                                        transition: 'background-color 0.2s',
                                                        opacity: editable ? 1 : 0.92
                                                    }}
                                                    onMouseEnter={(e) => { if (!isHighlighted) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                                                    onMouseLeave={(e) => { if (!isHighlighted) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                >
                                                    {/* Sno + lock indicator */}
                                                    <td style={{
                                                        padding: '0.35rem',
                                                        textAlign: 'center',
                                                        borderRight: '1px solid #e2e8f0',
                                                        fontWeight: 'bold',
                                                        color: '#64748b',
                                                        position: 'relative'
                                                    }}>
                                                        {index + 1}
                                                        {!editable && <Lock size={9} style={{ display: 'block', margin: '2px auto 0', color: '#94a3b8' }} />}
                                                    </td>

                                                    {/* DATE — first content column */}
                                                    <td style={{ padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? (
                                                            <CustomDatePicker
                                                                compact={true}
                                                                name="date"
                                                                value={evt.date || ''}
                                                                proposedMonth={evt.proposedMonth}
                                                                onChange={(e) => handleCellChange(evt.id, 'date', e.target.value)}
                                                            />
                                                        ) : roCell('date', formatDateForDisplay(evt.date))}
                                                    </td>

                                                    {/* END DATE */}
                                                    <td style={{ padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? (
                                                            <CustomDatePicker
                                                                compact={true}
                                                                name="toDate"
                                                                value={evt.toDate || ''}
                                                                proposedMonth={evt.proposedMonth}
                                                                minDate={evt.date || undefined}
                                                                onChange={(e) => handleCellChange(evt.id, 'toDate', e.target.value)}
                                                            />
                                                        ) : roCell('toDate', formatDateForDisplay(evt.toDate))}
                                                    </td>

                                                    {/* PROPOSED MONTH */}
                                                    <td style={{ padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? (
                                                            <select
                                                                value={evt.proposedMonth || ''}
                                                                onChange={(e) => handleCellChange(evt.id, 'proposedMonth', e.target.value)}
                                                                style={getSelectStyle('proposedMonth', evt.proposedMonth)}
                                                            >
                                                                <option value="" style={{ color: '#94a3b8' }}>— Select —</option>
                                                                {MONTH_OPTIONS.map(opt => <option key={opt} value={opt} style={{ background: '#fff', color: '#333' }}>{opt}</option>)}
                                                            </select>
                                                        ) : roCell('proposedMonth', evt.proposedMonth)}
                                                    </td>

                                                    {/* TIME */}
                                                    <td style={{ ...getCellColorStyle('time', evt.time), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                         {editable ? (() => {
                                                             const { start, end } = parseTimeRange(evt.time);
                                                             const isRangeValid = isValidTimeRange(evt.time);
                                                             return (
                                                                 <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
                                                                     <div style={{ display: 'flex', gap: '4px', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                                                         <CustomTimePicker
                                                                             compact={true}
                                                                             error={!isRangeValid}
                                                                             name="startTime"
                                                                             value={start}
                                                                             onChange={(e) => {
                                                                                 const newStart = e.target.value;
                                                                                 const newTimeStr = formatTimeRange(newStart, end);
                                                                                 handleCellChange(evt.id, 'time', newTimeStr);
                                                                             }}
                                                                         />
                                                                         <span style={{ fontSize: '0.7rem', color: '#7f6000', fontWeight: 'bold' }}>-</span>
                                                                         <CustomTimePicker
                                                                             compact={true}
                                                                             error={!isRangeValid}
                                                                             name="endTime"
                                                                             value={end}
                                                                             onChange={(e) => {
                                                                                 const newEnd = e.target.value;
                                                                                 const newTimeStr = formatTimeRange(start, newEnd);
                                                                                 handleCellChange(evt.id, 'time', newTimeStr);
                                                                             }}
                                                                         />
                                                                     </div>
                                                                     {!isRangeValid && (
                                                                         <span style={{ fontSize: '0.58rem', color: '#ef4444', fontWeight: '600' }} title="End time must be after start time">
                                                                             ⚠️ Invalid
                                                                         </span>
                                                                     )}
                                                                 </div>
                                                             );
                                                         })() : roCell('time', formatTimeForDisplay(evt.time))}
                                                    </td>

                                                    {/* TITLE */}
                                                    <td style={{ ...getCellColorStyle('title', evt.title), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? (
                                                            <textarea
                                                                value={evt.title || ''}
                                                                onChange={(e) => handleCellChange(evt.id, 'title', e.target.value)}
                                                                onBlur={() => handleCellBlur(evt.id)}
                                                                rows={2}
                                                                placeholder="Enter event title…"
                                                                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', resize: 'none', fontSize: '0.76rem', fontFamily: 'inherit', color: 'inherit', fontWeight: '600', lineHeight: '1.2' }}
                                                            />
                                                        ) : roCell('title', evt.title, { fontWeight: '600' })}
                                                    </td>

                                                    {/* VENUE */}
                                                    <td style={{ ...getCellColorStyle('venue', evt.venue), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                            {editable ? renderGridSelect('venue', evt.venue, VENUE_OPTIONS) : roCell('venue', evt.venue)}
                                                            {hasConflict(evt) && (
                                                                <span style={{
                                                                    alignSelf: 'flex-start',
                                                                    display: 'inline-flex', alignItems: 'center', gap: '2px',
                                                                    fontSize: '0.62rem', background: '#fee2e2', color: '#ef4444',
                                                                    border: '1px solid #fecaca', borderRadius: '4px',
                                                                    padding: '1px 4px', fontWeight: 700,
                                                                    whiteSpace: 'nowrap'
                                                                }} title="Double Booking: Another event is scheduled at this venue, date, and time!">
                                                                    ⚠️ Double Booked
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* MODE */}
                                                    <td style={{ ...getCellColorStyle('mode', evt.mode), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? (
                                                            <select
                                                                value={evt.mode || ''}
                                                                onChange={(e) => handleCellChange(evt.id, 'mode', e.target.value)}
                                                                style={getSelectStyle('mode', evt.mode)}
                                                            >
                                                                <option value="" style={{ color: '#94a3b8' }}>— Select —</option>
                                                                {MODE_OPTIONS.map(opt => <option key={opt} value={opt} style={{ background: '#fff', color: '#333' }}>{opt}</option>)}
                                                            </select>
                                                        ) : roCell('mode', evt.mode)}
                                                    </td>

                                                    {/* VERTICAL NAME */}
                                                    <td style={{ ...getCellColorStyle('verticalName', evt.verticalName), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? renderGridSelect('verticalName', evt.verticalName, VERTICAL_OPTIONS) : roCell('verticalName', evt.verticalName)}
                                                    </td>

                                                    {/* SUB VERTICAL */}
                                                    <td style={{ ...getCellColorStyle('subVertical', evt.subVertical), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? renderGridSelect('subVertical', evt.subVertical, SUB_VERTICAL_OPTIONS[evt.verticalName] || ['Other']) : roCell('subVertical', evt.subVertical)}
                                                    </td>

                                                    {/* CENTER NAME */}
                                                    <td style={{ ...getCellColorStyle('centerName', evt.centerName), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? renderGridSelect('centerName', evt.centerName, CENTER_OPTIONS) : roCell('centerName', evt.centerName)}
                                                    </td>

                                                    {/* COORDINATOR */}
                                                    <td style={{ ...getCellColorStyle('coordinator', evt.coordinator), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? renderGridSelect('coordinator', evt.coordinator, COORDINATOR_OPTIONS) : roCell('coordinator', evt.coordinator)}
                                                    </td>

                                                    {/* TYPE OF EVENT */}
                                                    <td style={{ ...getCellColorStyle('eventType', evt.eventType), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? renderGridSelect('eventType', evt.eventType, EVENT_TYPE_OPTIONS) : roCell('eventType', evt.eventType)}
                                                    </td>


                                                    {/* THEME */}
                                                    <td style={{ ...getCellColorStyle('theme', evt.theme), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? (
                                                            <textarea
                                                                value={evt.theme || ''}
                                                                onChange={(e) => handleCellChange(evt.id, 'theme', e.target.value)}
                                                                onBlur={() => handleCellBlur(evt.id)}
                                                                rows={2}
                                                                placeholder="Enter theme…"
                                                                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', resize: 'none', fontSize: '0.76rem', fontFamily: 'inherit', color: 'inherit', lineHeight: '1.2' }}
                                                            />
                                                        ) : roCell('theme', evt.theme)}
                                                    </td>

                                                    {/* EXPECTED COUNT */}
                                                    <td style={{ ...getCellColorStyle('participants', evt.participants), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? (
                                                            <input
                                                                type="number"
                                                                value={evt.participants || ''}
                                                                onChange={(e) => handleCellChange(evt.id, 'participants', e.target.value)}
                                                                onBlur={() => handleCellBlur(evt.id)}
                                                                min="0"
                                                                placeholder="0"
                                                                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontSize: '0.76rem', fontFamily: 'inherit', color: 'inherit', textAlign: 'center', fontWeight: 'bold' }}
                                                            />
                                                        ) : roCell('participants', evt.participants, { textAlign: 'center', fontWeight: 'bold' })}
                                                    </td>

                                                    {/* PARTNER ORGANISATION */}
                                                    <td style={{ ...getCellColorStyle('partnerOrganisation', evt.partnerOrganisation), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                        {editable ? (
                                                            <input
                                                                type="text"
                                                                value={evt.partnerOrganisation || ''}
                                                                placeholder="Partner org…"
                                                                onChange={(e) => handleCellChange(evt.id, 'partnerOrganisation', e.target.value)}
                                                                onBlur={() => handleCellBlur(evt.id)}
                                                                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontSize: '0.76rem', fontFamily: 'inherit', color: 'inherit' }}
                                                            />
                                                        ) : roCell('partnerOrganisation', evt.partnerOrganisation)}
                                                    </td>

                                                    {/* STATUS — last content column */}
                                                    <td style={{ ...getCellColorStyle('status', evt.status), padding: '0.2rem', borderRight: '1px solid #e2e8f0' }}>
                                                         {editable ? (
                                                             <select
                                                                 value={evt.status || 'Scheduled'}
                                                                 onChange={(e) => handleCellChange(evt.id, 'status', e.target.value)}
                                                                 style={getSelectStyle('status', evt.status)}
                                                             >
                                                                 {STATUS_OPTIONS.map(opt => <option key={opt} value={opt} style={{ background: '#fff', color: '#333' }}>{opt}</option>)}
                                                             </select>
                                                        ) : roCell('status', evt.status, { fontWeight: '700' })}
                                                    </td>

                                                    {/* SAVE COLUMN */}
                                                    <td style={{ textAlign: 'center', padding: '0.25rem', borderRight: '1px solid #e2e8f0', verticalAlign: 'middle' }}>
                                                        {!showArchived && !isDG && !evt.isLocked && (!evt.CreatedBy || String(evt.CreatedBy || evt.createdBy) === String(user?.userId || user?.sub || currentUsername)) ? (
                                                            <button
                                                                onClick={() => handleLockEvent(evt.id)}
                                                                title="Lock & Save Event (Permanent)"
                                                                style={{
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    color: '#f59e0b',
                                                                    cursor: 'pointer',
                                                                    padding: '0.25rem',
                                                                    borderRadius: '4px',
                                                                    transition: 'all 0.2s',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                            >
                                                                <Save size={14} />
                                                            </button>
                                                        ) : evt.isLocked ? (
                                                            <Lock size={12} color="#64748b" style={{ opacity: 0.8 }} title="Locked (Read-Only)" />
                                                        ) : (
                                                            <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>—</span>
                                                        )}
                                                    </td>

                                                    {/* ACTIONS COLUMN */}
                                                    <td style={{ textAlign: 'center', padding: '0.25rem', verticalAlign: 'middle' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
                                                            {/* View Details button — visible to all */}
                                                            <button
                                                                onClick={() => setActiveEventDetail(evt)}
                                                                title="View Details"
                                                                style={{
                                                                    position: 'relative',
                                                                    background: activeEventDetail?.id === evt.id ? '#eff6ff' : 'transparent',
                                                                    border: 'none',
                                                                    color: activeEventDetail?.id === evt.id ? '#2563eb' : '#64748b',
                                                                    cursor: 'pointer',
                                                                    padding: '0.25rem',
                                                                    borderRadius: '4px',
                                                                    transition: 'all 0.15s'
                                                                }}
                                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = activeEventDetail?.id === evt.id ? '#eff6ff' : 'transparent'; e.currentTarget.style.color = activeEventDetail?.id === evt.id ? '#2563eb' : '#64748b'; }}
                                                            >
                                                                <Eye size={14} />
                                                            </button>
                                                            {/* Comment button — visible to all */}
                                                            {!showArchived && (
                                                                <button
                                                                    onClick={() => { setCommentEventId(evt.id); setCommentDraft(''); }}
                                                                    title={commentCount > 0 ? `${commentCount} comment(s)` : 'Add comment'}
                                                                    style={{
                                                                        position: 'relative',
                                                                        background: commentEventId === evt.id ? '#dbeafe' : 'transparent',
                                                                        border: 'none',
                                                                        color: commentEventId === evt.id ? '#1d4ed8' : '#64748b',
                                                                        cursor: 'pointer',
                                                                        padding: '0.25rem',
                                                                        borderRadius: '4px',
                                                                        transition: 'all 0.15s'
                                                                    }}
                                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dbeafe'; e.currentTarget.style.color = '#1d4ed8'; }}
                                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = commentEventId === evt.id ? '#dbeafe' : 'transparent'; e.currentTarget.style.color = commentEventId === evt.id ? '#1d4ed8' : '#64748b'; }}
                                                                >
                                                                    <MessageSquare size={14} />
                                                                    {hasUnreadComments && (
                                                                        <span style={{
                                                                            position: 'absolute', top: '-3px', right: '-3px',
                                                                            background: '#3b82f6', color: 'white',
                                                                            borderRadius: '999px', fontSize: '0.58rem',
                                                                            fontWeight: 700, minWidth: '14px', height: '14px',
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            padding: '0 2px', lineHeight: 1
                                                                        }}>{commentCount > 9 ? '9+' : commentCount}</span>
                                                                    )}
                                                                </button>
                                                            )}
                                                            {/* Edit/Save Toggle — DG Only */}
                                                            {!showArchived && isDG && (
                                                                dgEditingRowId === evt.id ? (
                                                                    <button
                                                                        onClick={() => handleDgSaveRow(evt.id)}
                                                                        title="Save Event Row"
                                                                        style={{
                                                                            background: 'transparent',
                                                                            border: 'none',
                                                                            color: '#10b981',
                                                                            cursor: 'pointer',
                                                                            padding: '0.25rem',
                                                                            borderRadius: '4px',
                                                                            transition: 'all 0.2s'
                                                                        }}
                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                                    >
                                                                        <Check size={14} />
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => setDgEditingRowId(evt.id)}
                                                                        title="Edit Event Row"
                                                                        style={{
                                                                            background: 'transparent',
                                                                            border: 'none',
                                                                            color: '#2563eb',
                                                                            cursor: 'pointer',
                                                                            padding: '0.25rem',
                                                                            borderRadius: '4px',
                                                                            transition: 'all 0.2s'
                                                                        }}
                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                                    >
                                                                        <Edit size={14} />
                                                                    </button>
                                                                )
                                                            )}
                                                            {/* Delete — DG can always delete; others only if active and not locked */}
                                                            {(isDG || (!showArchived && isEditWindowOpen(evt))) && (
                                                                <button
                                                                    onClick={() => handleDeleteRow(evt.id)}
                                                                    title="Delete Event"
                                                                    style={{
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        color: '#ef4444',
                                                                        cursor: 'pointer',
                                                                        padding: '0.25rem',
                                                                        borderRadius: '4px',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    /* Calendar Tab */
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 340px',
                        gap: '1.25rem',
                        height: 'auto'
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '1.25rem',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            minHeight: '520px'
                        }}>
                            {/* Month Header controls */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid #f1f5f9',
                                paddingBottom: '0.75rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <button
                                        onClick={prevMonth}
                                        style={{
                                            border: '1px solid #e2e8f0', background: 'white',
                                            borderRadius: '8px', cursor: 'pointer', padding: '0.4rem',
                                            color: '#475569', display: 'flex', alignItems: 'center',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.color = '#8b5cf6'; e.currentTarget.style.transform = 'scale(1.05)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.transform = 'scale(1)'; }}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', minWidth: '150px', textAlign: 'center', fontFamily: 'inherit' }}>
                                        {monthName} {currentYear}
                                    </h2>
                                    <button
                                        onClick={nextMonth}
                                        style={{
                                            border: '1px solid #e2e8f0', background: 'white',
                                            borderRadius: '8px', cursor: 'pointer', padding: '0.4rem',
                                            color: '#475569', display: 'flex', alignItems: 'center',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.color = '#8b5cf6'; e.currentTarget.style.transform = 'scale(1.05)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.transform = 'scale(1)'; }}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                                <div style={{ display: 'flex', gap: '0.35rem' }}>
                                    <button
                                        onClick={() => setCurrentCalDate(new Date())}
                                        style={{
                                            padding: '0.45rem 1rem', borderRadius: '8px',
                                            border: '1px solid #e2e8f0',
                                            background: currentMonth === new Date().getMonth() && currentYear === new Date().getFullYear() ? '#f8fafc' : 'white',
                                            color: '#0f172a', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.color = '#8b5cf6'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#0f172a'; }}
                                    >
                                        Today
                                    </button>
                                </div>
                            </div>

                            {/* Calendar Grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                                gap: '6px',
                                flex: 1
                            }}>
                                {/* Weekday headers */}
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                                    const isWeekend = index === 0 || index === 6;
                                    return (
                                        <div 
                                            key={day} 
                                            style={{ 
                                                fontSize: '0.8rem', 
                                                fontWeight: 700, 
                                                color: isWeekend ? '#e11d48' : '#475569', 
                                                padding: '8px 0', 
                                                textAlign: 'center', 
                                                borderBottom: '2px solid #f1f5f9',
                                                backgroundColor: isWeekend ? '#fff1f2' : 'transparent',
                                                borderRadius: '6px'
                                            }}
                                        >
                                            {day}
                                        </div>
                                    );
                                })}

                                {/* Cells */}
                                {calendarDays.map((day, idx) => {
                                    if (day.isPadding) {
                                        return (
                                            <div 
                                                key={`pad-${idx}`} 
                                                style={{ 
                                                    background: '#f8fafc', 
                                                    borderRadius: '12px', 
                                                    minHeight: '100px', 
                                                    border: '1px solid #eaecf0',
                                                    opacity: 0.5 
                                                }} 
                                            />
                                        );
                                    }

                                    const isSelected = selectedCalDay === day.dateStr;
                                    const isToday = day.dateStr === todayDateStr;
                                    const hasEvents = day.events.length > 0;

                                    return (
                                        <div
                                            key={day.dateStr}
                                            onClick={() => setSelectedCalDay(isSelected ? null : day.dateStr)}
                                            style={{
                                                minHeight: '100px',
                                                background: isSelected ? '#f5f3ff' : (isToday ? '#faf5ff' : 'white'),
                                                border: isSelected ? '2px solid #8b5cf6' : (isToday ? '2px solid #c084fc' : '1px solid #e2e8f0'),
                                                borderRadius: '12px',
                                                padding: '0.5rem',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '0.35rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                position: 'relative',
                                                boxShadow: isSelected ? '0 4px 12px rgba(139, 92, 246, 0.12)' : 'none',
                                            }}
                                            onMouseEnter={(e) => { 
                                                if (!isSelected) {
                                                    e.currentTarget.style.borderColor = '#8b5cf6';
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                                                }
                                            }}
                                            onMouseLeave={(e) => { 
                                                if (!isSelected) {
                                                    e.currentTarget.style.borderColor = isToday ? '#c084fc' : '#e2e8f0';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                backgroundColor: isToday ? '#8b5cf6' : 'transparent',
                                                color: isToday ? 'white' : (hasEvents ? '#1e3a8a' : '#475569'),
                                                fontWeight: isToday || hasEvents ? '700' : '500',
                                                fontSize: '0.78rem',
                                                marginBottom: '0.15rem'
                                            }}>
                                                {day.dayNum}
                                            </div>

                                            {/* Scrollable events list in cell */}
                                            <div style={{
                                                flex: 1,
                                                overflowY: 'auto',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                {day.events.map(evt => {
                                                    const theme = getStatusTheme(evt.status);
                                                    return (
                                                        <div
                                                            key={evt.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveEventDetail(evt);
                                                            }}
                                                            style={{
                                                                fontSize: '0.68rem',
                                                                padding: '4px 6px',
                                                                borderRadius: '6px',
                                                                background: theme.bg,
                                                                color: theme.text,
                                                                fontWeight: 600,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                borderLeft: `3px solid ${theme.border}`,
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                                                transition: 'all 0.15s ease',
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.transform = 'scale(1.02)';
                                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.transform = 'scale(1)';
                                                                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
                                                            }}
                                                            title={`${evt.title}\nVenue: ${evt.venue}\nTime: ${formatTimeForDisplay(evt.time)}`}
                                                        >
                                                            {evt.title || 'Untitled'}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Right Details Panel */}
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '1.25rem',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            height: 'fit-content'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Info size={18} color="#0d9488" /> Calendar Details
                            </h3>

                            {selectedCalDay ? (
                                <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.78rem' }}>
                                    <span style={{ fontWeight: 600, color: '#334155' }}>
                                        Selected: {new Date(selectedCalDay).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span>
                                    <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.5rem' }}>
                                        {!showArchived && (
                                            <button
                                                onClick={() => handleAddRow(selectedCalDay)}
                                                style={{
                                                    flex: 1, padding: '0.4rem', borderRadius: '6px', border: 'none',
                                                    background: '#10b981', color: 'white', fontWeight: 600, cursor: 'pointer',
                                                    fontSize: '0.72rem', textAlign: 'center'
                                                }}
                                            >
                                                Add Event Here
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setSelectedCalDay(null)}
                                            style={{
                                                flex: showArchived ? 1 : 'initial',
                                                padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1',
                                                background: 'white', color: '#64748b', cursor: 'pointer', fontSize: '0.72rem',
                                                textAlign: 'center'
                                            }}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>
                                    Click a date cell to filter/add events.
                                </p>
                            )}

                            {/* Scheduled events list */}
                            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <strong style={{ fontSize: '0.78rem', color: '#475569' }}>
                                    Scheduled Events in View ({filteredEvents.length})
                                </strong>
                                {filteredEvents.map(evt => {
                                    const theme = getStatusTheme(evt.status);
                                    return (
                                        <div
                                            key={evt.id}
                                            onClick={() => setActiveEventDetail(evt)}
                                            style={{
                                                padding: '0.75rem 0.85rem 0.75rem 1.1rem',
                                                borderRadius: '10px',
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                cursor: 'pointer',
                                                fontSize: '0.78rem',
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                                            }}
                                            onMouseEnter={(e) => { 
                                                e.currentTarget.style.borderColor = theme.border; 
                                                e.currentTarget.style.background = theme.bg + '1e'; // ~12% opacity background match
                                                e.currentTarget.style.transform = 'translateX(4px)';
                                                e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.04)';
                                            }}
                                            onMouseLeave={(e) => { 
                                                e.currentTarget.style.borderColor = '#e2e8f0'; 
                                                e.currentTarget.style.background = '#ffffff'; 
                                                e.currentTarget.style.transform = 'translateX(0)';
                                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.02)';
                                            }}
                                        >
                                            {/* Left Status Color Strip */}
                                            <div style={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                bottom: 0,
                                                width: '4px',
                                                background: theme.border
                                            }} />
                                            <div style={{ fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {evt.title || 'Untitled Event'}
                                            </div>
                                            <div style={{ display: 'flex', justifycontent: 'space-between', color: '#64748b', fontSize: '0.72rem', marginTop: '0.35rem' }}>
                                                <span>
                                                    {evt.date ? new Date(evt.date).toLocaleDateString('en-GB') : 'N/A'} ({formatTimeForDisplay(evt.time)})
                                                </span>
                                                <span style={{ color: '#6366f1', fontWeight: 650 }}>
                                                    {evt.coordinator || 'Unassigned'}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Popup details/edit form for Calendar click */}
            {activeEventDetail && (() => {
                const modalEditable = isDG ? (dgEditingRowId === activeEventDetail.id) : (!activeEventDetail.isArchived && isEditWindowOpen(activeEventDetail));
                return (
                    <div 
                        onClick={handleCloseModal}
                        style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(15, 23, 42, 0.45)', display: 'flex', justifyContent: 'center',
                            alignItems: 'center', zIndex: 1100,
                            fontFamily: 'inherit',
                            backdropFilter: 'blur(4px)'
                        }}
                    >
                        <div 
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: 'white', padding: '0', borderRadius: '20px',
                                width: '90%', maxWidth: '580px', maxHeight: '92vh',
                                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
                                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                                border: '1px solid rgba(226, 232, 240, 0.8)'
                            }}
                        >
                        {/* Modal Header */}
                        <div style={{
                            padding: '1.25rem 1.75rem',
                            background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(255,255,255,0.08)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <FileSpreadsheet size={20} style={{ color: '#34d399' }} /> Event Information
                                </h3>
                                {activeEventDetail.status && (() => {
                                    const theme = getStatusTheme(activeEventDetail.status);
                                    return (
                                        <span style={{
                                            fontSize: '0.72rem',
                                            padding: '2px 8px',
                                            borderRadius: '20px',
                                            background: theme.bg,
                                            color: theme.text,
                                            fontWeight: 700,
                                            border: `1px solid ${theme.border}`,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.02em',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            {activeEventDetail.status}
                                        </span>
                                    );
                                })()}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                {isDG && (
                                    dgEditingRowId === activeEventDetail.id ? (
                                        <button
                                            onClick={() => handleDgSaveRow(activeEventDetail.id)}
                                            style={{
                                                background: '#10b981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '0.35rem 0.75rem',
                                                fontSize: '0.76rem',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
                                        >
                                            <Check size={13} /> Save
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setDgEditingRowId(activeEventDetail.id)}
                                            style={{
                                                background: '#2563eb',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '0.35rem 0.75rem',
                                                fontSize: '0.76rem',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                                        >
                                            <Edit size={13} /> Edit
                                        </button>
                                    )
                                )}
                                <button
                                    onClick={handleCloseModal}
                                    style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', opacity: 0.8 }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = 0.8}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* Lock/View notice in modal */}
                        {isDG && dgEditingRowId !== activeEventDetail.id && (
                            <div style={{
                                padding: '0.65rem 1.75rem',
                                background: '#eff6ff',
                                borderBottom: '1px solid #bfdbfe',
                                fontSize: '0.8rem',
                                color: '#1e3a8a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Info size={14} style={{ color: '#2563eb' }} />
                                This event is in view-only mode. Click Edit in the header to modify its details.
                            </div>
                        )}
                        {!isDG && !isEditWindowOpen(activeEventDetail) && (
                            <div style={{
                                padding: '0.65rem 1.75rem',
                                background: '#fef9c3',
                                borderBottom: '1px solid #fde68a',
                                fontSize: '0.8rem',
                                color: '#92400e',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <Lock size={14} />
                                This event is locked for editing. Use the 💬 comment button to request changes from the DG Office.
                            </div>
                        )}

                        {/* Modal Body / Form */}
                        <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.2rem', fontSize: '0.85rem' }}>
                            {/* Row 1: Event Title */}
                            <div>
                                <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Event Title</label>
                                <textarea
                                    value={activeEventDetail.title}
                                    onChange={(e) => handleCellChange(activeEventDetail.id, 'title', e.target.value)}
                                    onBlur={() => handleCellBlur(activeEventDetail.id)}
                                    rows={2}
                                    readOnly={!modalEditable}
                                    style={{
                                        width: '100%',
                                        padding: '0.65rem 0.75rem',
                                        borderRadius: '8px',
                                        border: modalEditable ? '1px solid #cbd5e1' : '1px solid #e2e8f0',
                                        outline: 'none',
                                        background: modalEditable ? '#ffffff' : '#f8fafc',
                                        color: modalEditable ? '#0f172a' : '#64748b',
                                        fontWeight: '600',
                                        resize: 'none',
                                        fontSize: '0.85rem',
                                        cursor: modalEditable ? 'text' : 'default',
                                        boxShadow: modalEditable ? 'inset 0 1px 2px rgba(0,0,0,0.02)' : 'none',
                                        transition: 'all 0.15s ease',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            {/* Row 2: Theme */}
                            <div>
                                <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Theme</label>
                                <textarea
                                    value={activeEventDetail.theme}
                                    onChange={(e) => handleCellChange(activeEventDetail.id, 'theme', e.target.value)}
                                    onBlur={() => handleCellBlur(activeEventDetail.id)}
                                    rows={2}
                                    readOnly={!modalEditable}
                                    style={{
                                        width: '100%',
                                        padding: '0.65rem 0.75rem',
                                        borderRadius: '8px',
                                        border: modalEditable ? '1px solid #cbd5e1' : '1px solid #e2e8f0',
                                        outline: 'none',
                                        background: modalEditable ? '#ffffff' : '#f8fafc',
                                        color: modalEditable ? '#0f172a' : '#64748b',
                                        resize: 'none',
                                        fontSize: '0.85rem',
                                        cursor: modalEditable ? 'text' : 'default',
                                        boxShadow: modalEditable ? 'inset 0 1px 2px rgba(0,0,0,0.02)' : 'none',
                                        transition: 'all 0.15s ease',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            {/* Row 3: Venue & Mode */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Venue</label>
                                    {renderModalSelect('venue', activeEventDetail.venue, VENUE_OPTIONS, {}, !modalEditable)}
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mode</label>
                                    {renderModalSelect('mode', activeEventDetail.mode, MODE_OPTIONS, {}, !modalEditable)}
                                </div>
                            </div>

                            {/* Row 4: Dates, Proposed Month & Time */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.5fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Date</label>
                                    {modalEditable ? (
                                        <CustomDatePicker
                                            name="date"
                                            value={activeEventDetail.date || ''}
                                            proposedMonth={activeEventDetail.proposedMonth}
                                            onChange={(e) => handleCellChange(activeEventDetail.id, 'date', e.target.value)}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={formatDateForDisplay(activeEventDetail.date)}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                padding: '0.65rem 0.75rem',
                                                borderRadius: '8px',
                                                border: '1px solid #e2e8f0',
                                                outline: 'none',
                                                fontSize: '0.82rem',
                                                background: '#f8fafc',
                                                color: '#64748b',
                                                cursor: 'default',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    )}
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>End Date</label>
                                    {modalEditable ? (
                                        <CustomDatePicker
                                            name="toDate"
                                            value={activeEventDetail.toDate || ''}
                                            proposedMonth={activeEventDetail.proposedMonth}
                                            minDate={activeEventDetail.date || undefined}
                                            onChange={(e) => handleCellChange(activeEventDetail.id, 'toDate', e.target.value)}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={formatDateForDisplay(activeEventDetail.toDate)}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                padding: '0.65rem 0.75rem',
                                                borderRadius: '8px',
                                                border: '1px solid #e2e8f0',
                                                outline: 'none',
                                                fontSize: '0.82rem',
                                                background: '#f8fafc',
                                                color: '#64748b',
                                                cursor: 'default',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    )}
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Time</label>
                                    {modalEditable ? (() => {
                                         const { start, end } = parseTimeRange(activeEventDetail.time);
                                         const isRangeValid = isValidTimeRange(activeEventDetail.time);
                                         return (
                                             <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                 <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                     <CustomTimePicker
                                                         error={!isRangeValid}
                                                         name="startTime"
                                                         value={start}
                                                         onChange={(e) => {
                                                             const newStart = e.target.value;
                                                             const newTimeStr = formatTimeRange(newStart, end);
                                                             handleCellChange(activeEventDetail.id, 'time', newTimeStr);
                                                         }}
                                                     />
                                                     <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>to</span>
                                                     <CustomTimePicker
                                                         error={!isRangeValid}
                                                         name="endTime"
                                                         value={end}
                                                         onChange={(e) => {
                                                             const newEnd = e.target.value;
                                                             const newTimeStr = formatTimeRange(start, newEnd);
                                                             handleCellChange(activeEventDetail.id, 'time', newTimeStr);
                                                         }}
                                                     />
                                                 </div>
                                                 {!isRangeValid && (
                                                     <span style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                         ⚠️ End time must be after start time
                                                     </span>
                                                 )}
                                             </div>
                                         );
                                     })() : (
                                         <input
                                             type="text"
                                             value={formatTimeForDisplay(activeEventDetail.time)}
                                             readOnly
                                             style={{
                                                 width: '100%',
                                                 padding: '0.65rem 0.75rem',
                                                 borderRadius: '8px',
                                                 border: '1px solid #e2e8f0',
                                                 outline: 'none',
                                                 background: '#f8fafc',
                                                 color: '#64748b',
                                                 fontWeight: '600',
                                                 fontSize: '0.82rem',
                                                 cursor: 'default',
                                                 transition: 'all 0.15s ease',
                                                 boxSizing: 'border-box'
                                             }}
                                         />
                                     )}
                                 </div>
                            </div>

                            {/* Row 5: Vertical & Sub Vertical */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Vertical</label>
                                    {renderModalSelect('verticalName', activeEventDetail.verticalName, VERTICAL_OPTIONS, {}, !modalEditable)}
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sub Vertical</label>
                                    {renderModalSelect('subVertical', activeEventDetail.subVertical, SUB_VERTICAL_OPTIONS[activeEventDetail.verticalName] || ['Other'], {}, !modalEditable)}
                                </div>
                            </div>

                            {/* Row 6: Center & Coordinator */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Center</label>
                                    {renderModalSelect('centerName', activeEventDetail.centerName, CENTER_OPTIONS, {}, !modalEditable)}
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Coordinator</label>
                                    {renderModalSelect('coordinator', activeEventDetail.coordinator, COORDINATOR_OPTIONS, {}, !modalEditable)}
                                </div>
                            </div>

                            {/* Row 7: Type of Event & Status */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type of Event</label>
                                    {renderModalSelect('eventType', activeEventDetail.eventType, EVENT_TYPE_OPTIONS, {}, !modalEditable)}
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</label>
                                    {renderModalSelect('status', activeEventDetail.status, STATUS_OPTIONS, {}, !modalEditable)}
                                </div>
                            </div>

                            {/* Row 8: Expected Count & Partner Organisation */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Expected Count</label>
                                    <input
                                        type="number"
                                        value={activeEventDetail.participants}
                                        onChange={(e) => handleCellChange(activeEventDetail.id, 'participants', e.target.value)}
                                        onBlur={() => handleCellBlur(activeEventDetail.id)}
                                        readOnly={!modalEditable}
                                        min="0"
                                        style={{
                                            width: '100%',
                                            padding: '0.65rem 0.75rem',
                                            borderRadius: '8px',
                                            border: modalEditable ? '1px solid #cbd5e1' : '1px solid #e2e8f0',
                                            background: modalEditable ? '#ffffff' : '#f8fafc',
                                            color: modalEditable ? '#0f172a' : '#64748b',
                                            fontWeight: '600',
                                            textAlign: 'center',
                                            fontSize: '0.82rem',
                                            outline: 'none',
                                            cursor: modalEditable ? 'text' : 'default',
                                            transition: 'all 0.15s ease',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Partner Organisation</label>
                                    <input
                                        type="text"
                                        value={activeEventDetail.partnerOrganisation}
                                        onChange={(e) => handleCellChange(activeEventDetail.id, 'partnerOrganisation', e.target.value)}
                                        onBlur={() => handleCellBlur(activeEventDetail.id)}
                                        readOnly={!modalEditable}
                                        style={{
                                            width: '100%',
                                            padding: '0.65rem 0.75rem',
                                            borderRadius: '8px',
                                            border: modalEditable ? '1px solid #cbd5e1' : '1px solid #e2e8f0',
                                            background: modalEditable ? '#ffffff' : '#f8fafc',
                                            color: modalEditable ? '#0f172a' : '#64748b',
                                            outline: 'none',
                                            fontSize: '0.82rem',
                                            cursor: modalEditable ? 'text' : 'default',
                                            transition: 'all 0.15s ease',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            padding: '1rem 1.75rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            {(isDG || (!activeEventDetail.isArchived && isEditWindowOpen(activeEventDetail))) ? (
                                <button
                                    onClick={() => handleDeleteRow(activeEventDetail.id)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.25rem',
                                        padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #fecaca',
                                        background: '#fef2f2', color: '#ef4444', fontWeight: 600, cursor: 'pointer',
                                        fontSize: '0.78rem', transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#fca5a533'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = '#fef2f2'}
                                >
                                    <Trash2 size={14} /> Delete Event
                                </button>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: '#64748b' }}>
                                    <Lock size={13} /> View only — contact DG Office to make changes
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                {/* Lock & Save button */}
                                {!isDG && !activeEventDetail.isArchived && !activeEventDetail.isLocked && (!activeEventDetail.CreatedBy || String(activeEventDetail.CreatedBy || activeEventDetail.createdBy) === String(user?.userId || user?.sub || currentUsername)) && (
                                    <button
                                        onClick={() => handleLockEvent(activeEventDetail.id)}
                                        style={{
                                            padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none',
                                            background: '#f59e0b', color: 'white', fontWeight: 600, cursor: 'pointer',
                                            fontSize: '0.78rem', transition: 'all 0.2s',
                                            display: 'flex', alignItems: 'center', gap: '0.35rem'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#d97706'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = '#f59e0b'}
                                    >
                                        <Save size={14} /> Lock & Save Event
                                    </button>
                                )}
                                <button
                                    onClick={() => handleJumpToRow(activeEventDetail)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.25rem',
                                        padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1',
                                        background: 'white', color: '#334155', fontWeight: 500, cursor: 'pointer',
                                        fontSize: '0.78rem', transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                    <Eye size={14} /> View in Spreadsheet
                                </button>
                                <button
                                    onClick={handleCloseModal}
                                    style={{
                                        padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none',
                                        background: '#0f172a', color: 'white', fontWeight: 600, cursor: 'pointer',
                                        fontSize: '0.78rem', transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#1e293b'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = '#0f172a'}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* ══════════════════════════════════════════════
                 COMMENT PANEL — slide-in overlay
            ══════════════════════════════════════════════ */}
            {commentEventId && (() => {
                const commentEvt = events.find(e => e.id === commentEventId);
                if (!commentEvt) return null;
                const comments = commentEvt.comments || [];

                return (
                    <div
                        style={{
                            position: 'fixed', top: 0, right: 0, bottom: 0,
                            width: '360px', background: 'white',
                            boxShadow: '-4px 0 30px rgba(0,0,0,0.12)',
                            display: 'flex', flexDirection: 'column',
                            zIndex: 1200,
                            fontFamily: "'Inter', -apple-system, sans-serif",
                            borderLeft: '1px solid #e2e8f0'
                        }}
                    >
                        {/* Panel Header */}
                        <div style={{
                            padding: '1rem 1.25rem',
                            background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                            color: 'white', display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', flexShrink: 0
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <MessageSquare size={18} style={{ color: '#93c5fd' }} />
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>Comments</div>
                                    <div style={{ fontSize: '0.7rem', opacity: 0.8, maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {commentEvt.title || 'Untitled Event'}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setCommentEventId(null)}
                                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', opacity: 0.8, display: 'flex', alignItems: 'center' }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.8}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Lock banner for non-editable events */}
                        {!isEditWindowOpen(commentEvt) && !isDG && (
                            <div style={{
                                padding: '0.55rem 1rem',
                                background: '#fef9c3',
                                borderBottom: '1px solid #fde68a',
                                fontSize: '0.75rem',
                                color: '#92400e',
                                display: 'flex', alignItems: 'center', gap: '0.4rem',
                                flexShrink: 0
                            }}>
                                <Lock size={12} /> This event is locked for editing. Leave a comment to request changes from the DG Office.
                            </div>
                        )}

                        {/* Comments list */}
                        <div style={{
                            flex: 1, overflowY: 'auto',
                            padding: '1rem',
                            display: 'flex', flexDirection: 'column', gap: '0.75rem'
                        }}>
                            {comments.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem', marginTop: '2rem' }}>
                                    <MessageSquare size={32} style={{ opacity: 0.3, display: 'block', margin: '0 auto 0.5rem' }} />
                                    No comments yet. Be the first to add one.
                                </div>
                            ) : (
                                comments.map((c) => {
                                    const initials = (c.authorName || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                                    const postedDate = new Date(c.postedAt);
                                    const dateLabel = postedDate.toLocaleString('en-IN', {
                                        timeZone: 'Asia/Kolkata',
                                        day: 'numeric', month: 'short',
                                        hour: '2-digit', minute: '2-digit'
                                    });
                                    return (
                                        <div key={c.id} style={{
                                            display: 'flex', gap: '0.65rem', alignItems: 'flex-start'
                                        }}>
                                            <div style={{
                                                width: '32px', height: '32px', borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #1e3a8a, #0d9488)',
                                                color: 'white', fontWeight: 700,
                                                fontSize: '0.72rem', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0
                                            }}>{initials}</div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.2rem' }}>
                                                    <span style={{ fontWeight: 700, fontSize: '0.78rem', color: '#1e293b' }}>{c.authorName}</span>
                                                    <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{dateLabel}</span>
                                                </div>
                                                <div style={{
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '0 8px 8px 8px',
                                                    padding: '0.5rem 0.75rem',
                                                    fontSize: '0.8rem', color: '#334155',
                                                    lineHeight: 1.5,
                                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                                                }}>{c.text}</div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={commentEndRef} />
                        </div>

                        {/* Comment input */}
                        <div style={{
                            padding: '0.85rem 1rem',
                            borderTop: '1px solid #e2e8f0',
                            background: '#f8fafc',
                            flexShrink: 0
                        }}>
                            <textarea
                                value={commentDraft}
                                onChange={(e) => setCommentDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleAddComment(commentEventId);
                                    }
                                }}
                                placeholder="Type a comment... (Enter to send, Shift+Enter for newline)"
                                rows={3}
                                style={{
                                    width: '100%', boxSizing: 'border-box',
                                    padding: '0.6rem 0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.82rem',
                                    fontFamily: 'inherit',
                                    resize: 'none',
                                    outline: 'none',
                                    color: '#1e293b',
                                    lineHeight: 1.5,
                                    transition: 'border-color 0.15s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                                <button
                                    onClick={() => handleAddComment(commentEventId)}
                                    disabled={!commentDraft.trim()}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                                        padding: '0.5rem 1rem', borderRadius: '8px', border: 'none',
                                        background: commentDraft.trim() ? '#1e3a8a' : '#e2e8f0',
                                        color: commentDraft.trim() ? 'white' : '#94a3b8',
                                        fontWeight: 600, fontSize: '0.8rem',
                                        cursor: commentDraft.trim() ? 'pointer' : 'not-allowed',
                                        transition: 'all 0.15s'
                                    }}
                                    onMouseEnter={(e) => { if (commentDraft.trim()) e.currentTarget.style.background = '#1e40af'; }}
                                    onMouseLeave={(e) => { if (commentDraft.trim()) e.currentTarget.style.background = '#1e3a8a'; }}
                                >
                                    <Send size={14} /> Post Comment
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
            {confirmModal && (() => {
                const isDelete = confirmModal.type === 'delete';
                return (
                    <div 
                        onClick={() => setConfirmModal(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(15, 23, 42, 0.45)', display: 'flex', justifyContent: 'center',
                            alignItems: 'center', zIndex: 1200,
                            fontFamily: "'Inter', -apple-system, sans-serif",
                            backdropFilter: 'blur(4px)'
                        }}
                    >
                        <div 
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: 'white', padding: '1.75rem', borderRadius: '16px',
                                width: '90%', maxWidth: confirmModal.evtDetails ? '540px' : '400px',
                                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                                display: 'flex', flexDirection: 'column', gap: '1.25rem',
                                border: '1px solid #e2e8f0'
                            }}
                        >
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '50%',
                                    background: isDelete ? '#fee2e2' : '#fef3c7',
                                    color: isDelete ? '#ef4444' : '#d97706',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    {isDelete ? <AlertTriangle size={20} /> : <Lock size={20} />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#0f172a', fontSize: '1.1rem', fontWeight: 700 }}>
                                        {confirmModal.title}
                                    </h3>
                                    <p style={{ margin: 0, color: '#475569', fontSize: '0.85rem', lineHeight: 1.5 }}>
                                        {confirmModal.message}
                                    </p>
                                </div>
                            </div>

                            {confirmModal.evtDetails && (() => {
                                const d = confirmModal.evtDetails;
                                const detailsList = [
                                    { label: 'Title', val: d.title },
                                    { label: 'Date', val: d.date ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB') : '—' },
                                    { label: 'Time', val: formatTimeForDisplay(d.time) },
                                    { label: 'Venue', val: d.venue },
                                    { label: 'Mode', val: d.mode },
                                    { label: 'Vertical', val: d.verticalName },
                                    { label: 'Sub Vertical', val: d.subVertical },
                                    { label: 'Center', val: d.centerName },
                                    { label: 'Coordinator', val: d.coordinator },
                                    { label: 'Event Type', val: d.eventType },
                                    { label: 'Theme', val: d.theme },
                                    { label: 'Expected Count', val: d.participants },
                                    { label: 'Status', val: d.status },
                                    { label: 'Partner Org', val: d.partnerOrganisation }
                                ];
                                return (
                                    <div style={{
                                        maxHeight: '260px', overflowY: 'auto',
                                        background: '#f8fafc', border: '1px solid #e2e8f0',
                                        borderRadius: '8px', padding: '0.75rem',
                                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem 1rem'
                                    }}>
                                        {detailsList.map((item, idx) => (
                                            <div key={idx} style={{ fontSize: '0.76rem' }}>
                                                <div style={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: '0.05em' }}>
                                                    {item.label}
                                                </div>
                                                <div style={{ color: '#0f172a', fontWeight: 500, marginTop: '2px', wordBreak: 'break-word' }}>
                                                    {item.val || '—'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.25rem' }}>
                                <button
                                    onClick={() => setConfirmModal(null)}
                                    style={{
                                        padding: '0.55rem 1.25rem', borderRadius: '8px', border: '1px solid #cbd5e1',
                                        background: 'white', color: '#475569', fontWeight: 600, cursor: 'pointer',
                                        fontSize: '0.8rem', transition: 'all 0.15s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmModal.onConfirm}
                                    style={{
                                        padding: '0.55rem 1.25rem', borderRadius: '8px', border: 'none',
                                        background: isDelete ? '#ef4444' : '#d97706',
                                        color: 'white', fontWeight: 600, cursor: 'pointer',
                                        fontSize: '0.8rem', transition: 'all 0.15s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = isDelete ? '#dc2626' : '#b45309'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = isDelete ? '#ef4444' : '#d97706'}
                                >
                                    {isDelete ? 'Delete Permanent' : 'Confirm & Lock'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default EventPlanner;
